/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from "react";

function useTweaks(defaults) {
  const [values, setValues] = useState(defaults);
  const setTweak = useCallback((keyOrEdits, val) => {
    const edits = typeof keyOrEdits === "object" && keyOrEdits !== null
      ? keyOrEdits : { [keyOrEdits]: val };
    setValues(prev => ({ ...prev, ...edits }));
  }, []);
  return [values, setTweak];
}


/* ────────────────────────────────────────────────────────────────
   DIA CRM — landing content (6 langues + pricing par pays)
   ──────────────────────────────────────────────────────────────── */

const LANDING_CONTENT = {
  fr: {
    nav: { login: "Se connecter", trial: "Essai gratuit 24h" },
    hero: {
      online: "DIA est en ligne",
      rotate1: ["Votre business", "Votre hôtel", "Votre restaurant", "Votre salon", "Votre clinique"],
      rotate2: ["se gère tout seul", "fait vos check-ins", "prend des réservations", "remplit votre agenda", "confirme vos rdvs"],
      rotate3: "même quand vous dormez.",
      subtitle: "DIA répond et convertit vos clients sur WhatsApp, dans les commentaires Facebook et Instagram, et en DMs.",
      channelActive: "actif",
      channelSoon: "bientôt",
      stats: [
        { v: "500+", l: "business" },
        { v: "50+", l: "langues" },
        { v: "24h/24", l: "disponible" },
        { v: "5 min", l: "pour démarrer" },
      ],
      cta: "Tester 24h gratuitement",
    },
    languages: {
      title: "Une IA qui parle vraiment leur langue",
      subtitle: "DIA maîtrise le ton et les nuances locales.",
      tabs: { priority: "Prioritaires", african: "Africaines", international: "Internationales" },
    },
    sectors: {
      title: "Conçu pour votre métier",
      subtitle: "DIA s'adapte à différentes situations avec style.",
      items: [
        { id: "hotel",      name: "Hôtel",      ex: "« Vous avez une chambre pour ce week-end ? »" },
        { id: "restaurant", name: "Restaurant", ex: "« Une table pour 4 ce soir à 20h ? »" },
        { id: "boutique",   name: "Boutique",   ex: "« Vous avez ce modèle en taille M ? »" },
        { id: "salon",      name: "Salon",      ex: "« RDV pour une coupe demain ? »" },
        { id: "clinique",   name: "Clinique",   ex: "« Premier RDV pour une consultation ? »" },
        { id: "autre",      name: "Autre",      ex: "« On configure ensemble votre cas ? »" },
      ],
      try: "Tester en direct",
    },
    pricing: {
      title: "Un prix par taille de business",
      subtitle: "Sans engagement, sans paiement pour l'essai.",
      perMonth: "/ mois",
      mostPopular: "Le plus choisi",
      onQuote: "Sur devis",
      cta: "Démarrer",
      ctaPro: "Choisir Pro",
      ctaPremium: "Nous contacter",
      detected: "Prix affichés pour",
      change: "changer",
    },
    finalCta: {
      title: "24 heures pour voir DIA répondre à vos vrais clients.",
      subtitle: "Pas d'installation. Configuration en 5 minutes via WhatsApp.",
      button: "Tester 24h gratuitement",
      tiny: "Aucune carte bancaire requise",
    },
    footer: { rights: "Tous droits réservés", made: "Conçu par amour pour votre Business." },
    common: { showMore: "Voir plus", showLess: "Voir moins" },
  },

  en: {
    nav: { login: "Sign in", trial: "24h free trial" },
    hero: {
      online: "DIA is online",
      rotate1: ["Your shop", "Your hotel", "Your restaurant", "Your salon", "Your clinic"],
      rotate2: ["sells", "checks guests in", "takes reservations", "fills your agenda", "confirms appointments"],
      rotate3: "even while you sleep.",
      subtitle: "DIA replies, convinces and converts your customers in their own language — on WhatsApp, in Facebook & Instagram comments, and in DMs.",
      channelActive: "live",
      channelSoon: "soon",
      stats: [
        { v: "500+", l: "businesses" },
        { v: "50+", l: "languages" },
        { v: "24/7", l: "available" },
        { v: "5 min", l: "to set up" },
      ],
      cta: "Start 24h free trial",
    },
    languages: {
      title: "An AI that actually speaks their language",
      subtitle: "Not bland machine translation. DIA masters local expressions, tone and nuance.",
      tabs: { priority: "Priority", african: "African", international: "International" },
    },
    sectors: {
      title: "Built for your business",
      subtitle: "DIA adapts to your industry with pre-trained scenarios. Tap one to try it live.",
      items: [
        { id: "hotel",      name: "Hotel",      ex: "“Got a room available this weekend?”" },
        { id: "restaurant", name: "Restaurant", ex: "“Table for 4 tonight at 8pm?”" },
        { id: "boutique",   name: "Shop",       ex: "“Do you have this in size M?”" },
        { id: "salon",      name: "Salon",      ex: "“Booking for a haircut tomorrow?”" },
        { id: "clinique",   name: "Clinic",     ex: "“First-time consultation appointment?”" },
        { id: "autre",      name: "Other",      ex: "“Let's set up your custom case.”" },
      ],
      try: "Try it live",
    },
    pricing: {
      title: "One price per business size",
      subtitle: "No commitment, no card required for the trial.",
      perMonth: "/ month",
      mostPopular: "Most chosen",
      onQuote: "Custom quote",
      cta: "Start",
      ctaPro: "Choose Pro",
      ctaPremium: "Talk to us",
      detected: "Prices shown for",
      change: "change",
    },
    finalCta: {
      title: "24 hours to watch DIA reply to your real customers.",
      subtitle: "No credit card. No install. Setup in 5 minutes via WhatsApp.",
      button: "Start 24h free trial",
      tiny: "No credit card required",
    },
    footer: { rights: "All rights reserved", made: "Built for Africa. Made for the world." },
    common: { showMore: "Show more", showLess: "Show less" },
  },

  ar: {
    nav: { login: "تسجيل الدخول", trial: "تجربة مجانية ٢٤ ساعة" },
    hero: {
      online: "هيلوديا متصل الآن",
      rotate1: ["متجرك", "فندقك", "مطعمك", "صالونك", "عيادتك"],
      rotate2: ["يبيع", "يستقبل النزلاء", "يأخذ الحجوزات", "يملأ أجندتك", "يؤكد المواعيد"],
      rotate3: "حتى وأنت نائم.",
      subtitle: "هيلوديا يرد ويُقنع ويحوّل عملاءك بلغتهم — على واتساب، في التعليقات وفي الرسائل الخاصة.",
      channelActive: "نشط",
      channelSoon: "قريبا",
      stats: [
        { v: "+500", l: "نشاط تجاري" },
        { v: "+50", l: "لغة" },
        { v: "24/7", l: "متاح" },
        { v: "5 دق", l: "للبدء" },
      ],
      cta: "ابدأ تجربة ٢٤ ساعة",
    },
    languages: {
      title: "ذكاء اصطناعي يتحدث لغتهم بالفعل",
      subtitle: "ليست ترجمة آلية باهتة. هيلوديا يتقن التعابير والنبرة المحلية.",
      tabs: { priority: "الأساسية", african: "الإفريقية", international: "الدولية" },
    },
    sectors: {
      title: "مصمَّم لعملك",
      subtitle: "يتكيّف هيلوديا مع نشاطك بسيناريوهات جاهزة. اضغط للتجربة المباشرة.",
      items: [
        { id: "hotel",      name: "فندق",   ex: "«عندكم غرفة لهذا الأسبوع؟»" },
        { id: "restaurant", name: "مطعم",   ex: "«طاولة لـ ٤ أشخاص الليلة؟»" },
        { id: "boutique",   name: "متجر",   ex: "«عندكم هذا الموديل بمقاس M؟»" },
        { id: "salon",      name: "صالون",  ex: "«موعد قص شعر غدا؟»" },
        { id: "clinique",   name: "عيادة",  ex: "«موعد لاستشارة أولى؟»" },
        { id: "autre",      name: "آخر",    ex: "«نضبط حالتك معا؟»" },
      ],
      try: "جرّب الآن",
    },
    pricing: {
      title: "سعر لكل حجم نشاط",
      subtitle: "بدون التزام، بدون بطاقة بنكية للتجربة.",
      perMonth: "/ شهريا",
      mostPopular: "الأكثر اختيارا",
      onQuote: "عرض مخصّص",
      cta: "ابدأ",
      ctaPro: "اختر Pro",
      ctaPremium: "تواصل معنا",
      detected: "الأسعار المعروضة لـ",
      change: "تغيير",
    },
    finalCta: {
      title: "٢٤ ساعة لترى هيلوديا يرد على عملائك الحقيقيين.",
      subtitle: "بدون بطاقة بنكية. بدون تثبيت. الإعداد في ٥ دقائق عبر واتساب.",
      button: "ابدأ تجربة ٢٤ ساعة",
      tiny: "بدون بطاقة بنكية",
    },
    footer: { rights: "جميع الحقوق محفوظة", made: "صُمّم لإفريقيا. للعالم." },
    common: { showMore: "عرض المزيد", showLess: "عرض أقل" },
  },

  es: {
    nav: { login: "Iniciar sesión", trial: "Prueba 24h gratis" },
    hero: {
      online: "DIA está en línea",
      rotate1: ["Tu tienda", "Tu hotel", "Tu restaurante", "Tu salón", "Tu clínica"],
      rotate2: ["vende", "registra huéspedes", "toma reservas", "llena tu agenda", "confirma citas"],
      rotate3: "incluso mientras duermes.",
      subtitle: "DIA responde, convence y convierte a tus clientes en su idioma — en WhatsApp, en comentarios de Facebook e Instagram, y en DMs.",
      channelActive: "activo",
      channelSoon: "pronto",
      stats: [{v:"500+",l:"negocios"},{v:"50+",l:"idiomas"},{v:"24/7",l:"disponible"},{v:"5 min",l:"para empezar"}],
      cta: "Probar 24h gratis",
    },
    languages: { title: "Una IA que sí habla su idioma", subtitle: "Sin traducciones planas. DIA domina expresiones, tono y matices locales.", tabs: { priority:"Prioritarios", african:"Africanos", international:"Internacionales" } },
    sectors: {
      title: "Pensado para tu negocio",
      subtitle: "DIA se adapta con escenarios pre-entrenados. Toca uno para probarlo.",
      items: [
        { id:"hotel", name:"Hotel", ex:"«¿Tienen habitación para este finde?»" },
        { id:"restaurant", name:"Restaurante", ex:"«¿Mesa para 4 a las 20h?»" },
        { id:"boutique", name:"Tienda", ex:"«¿Tienen esto en talla M?»" },
        { id:"salon", name:"Salón", ex:"«¿Cita para un corte mañana?»" },
        { id:"clinique", name:"Clínica", ex:"«¿Primera consulta disponible?»" },
        { id:"autre", name:"Otro", ex:"«Configuremos tu caso.»" },
      ],
      try: "Probar en vivo",
    },
    pricing: { title:"Un precio por tamaño de negocio", subtitle:"Sin compromiso, sin tarjeta para la prueba.", perMonth:"/ mes", mostPopular:"Más elegido", onQuote:"A medida", cta:"Empezar", ctaPro:"Elegir Pro", ctaPremium:"Contáctanos", detected:"Precios mostrados para", change:"cambiar" },
    finalCta: { title:"24 horas para ver a DIA responder a tus clientes reales.", subtitle:"Sin tarjeta. Sin instalación. Configuración en 5 minutos por WhatsApp.", button:"Probar 24h gratis", tiny:"Sin tarjeta de crédito" },
    footer: { rights:"Todos los derechos reservados", made:"Hecho para África. Pensado para el mundo." },
    common: { showMore: "Ver más", showLess: "Ver menos" },
  },

  pt: {
    nav: { login: "Entrar", trial: "Teste grátis 24h" },
    hero: {
      online: "DIA está online",
      rotate1: ["A tua loja","O teu hotel","O teu restaurante","O teu salão","A tua clínica"],
      rotate2: ["vende","faz check-ins","aceita reservas","preenche a tua agenda","confirma marcações"],
      rotate3: "mesmo enquanto dormes.",
      subtitle: "DIA responde, convence e converte os teus clientes na língua deles — no WhatsApp, em comentários do Facebook e Instagram, e em DMs.",
      channelActive: "ativo",
      channelSoon: "em breve",
      stats: [{v:"500+",l:"negócios"},{v:"50+",l:"idiomas"},{v:"24/7",l:"disponível"},{v:"5 min",l:"para começar"}],
      cta: "Testar 24h grátis",
    },
    languages: { title:"Uma IA que fala mesmo a língua deles", subtitle:"Sem tradução automática chata. DIA domina expressões locais.", tabs:{priority:"Prioritários",african:"Africanos",international:"Internacionais"} },
    sectors: {
      title: "Pensado para o teu negócio",
      subtitle: "DIA adapta-se com cenários pré-treinados. Toca para testar.",
      items: [
        { id:"hotel", name:"Hotel", ex:"«Têm quarto este fim de semana?»" },
        { id:"restaurant", name:"Restaurante", ex:"«Mesa para 4 às 20h?»" },
        { id:"boutique", name:"Loja", ex:"«Tem isto em tamanho M?»" },
        { id:"salon", name:"Salão", ex:"«Marcação para um corte amanhã?»" },
        { id:"clinique", name:"Clínica", ex:"«Primeira consulta?»" },
        { id:"autre", name:"Outro", ex:"«Vamos configurar o teu caso.»" },
      ],
      try: "Testar ao vivo",
    },
    pricing: { title:"Um preço por tamanho de negócio", subtitle:"Sem compromisso, sem cartão para o teste.", perMonth:"/ mês", mostPopular:"Mais escolhido", onQuote:"Sob consulta", cta:"Começar", ctaPro:"Escolher Pro", ctaPremium:"Contactar", detected:"Preços para", change:"mudar" },
    finalCta: { title:"24 horas para ver DIA a responder aos teus clientes.", subtitle:"Sem cartão. Sem instalação. 5 minutos via WhatsApp.", button:"Testar 24h grátis", tiny:"Sem cartão necessário" },
    footer: { rights:"Todos os direitos reservados", made:"Feito para África. Para o mundo." },
    common: { showMore: "Ver mais", showLess: "Ver menos" },
  },

  de: {
    nav: { login: "Anmelden", trial: "24h kostenlos" },
    hero: {
      online: "DIA ist online",
      rotate1: ["Dein Shop","Dein Hotel","Dein Restaurant","Dein Salon","Deine Klinik"],
      rotate2: ["verkauft","checkt Gäste ein","nimmt Reservierungen an","füllt deinen Kalender","bestätigt Termine"],
      rotate3: "auch im Schlaf.",
      subtitle: "DIA antwortet, überzeugt und konvertiert Kund:innen in ihrer Sprache — auf WhatsApp, in Facebook- und Instagram-Kommentaren und in DMs.",
      channelActive: "aktiv",
      channelSoon: "bald",
      stats: [{v:"500+",l:"Unternehmen"},{v:"50+",l:"Sprachen"},{v:"24/7",l:"verfügbar"},{v:"5 Min",l:"Setup"}],
      cta: "24h kostenlos starten",
    },
    languages: { title:"Eine KI, die ihre Sprache wirklich spricht", subtitle:"Keine flache Maschinenübersetzung. DIA beherrscht lokale Wendungen.", tabs:{priority:"Priorität",african:"Afrikanisch",international:"International"} },
    sectors: {
      title: "Gemacht für dein Business",
      subtitle: "DIA passt sich mit fertigen Szenarien an. Antippen zum Testen.",
      items: [
        { id:"hotel", name:"Hotel", ex:"„Zimmer dieses Wochenende frei?“" },
        { id:"restaurant", name:"Restaurant", ex:"„Tisch für 4 um 20 Uhr?“" },
        { id:"boutique", name:"Shop", ex:"„Habt ihr das in M?“" },
        { id:"salon", name:"Salon", ex:"„Termin für einen Schnitt morgen?“" },
        { id:"clinique", name:"Praxis", ex:"„Ersttermin möglich?“" },
        { id:"autre", name:"Anderes", ex:"„Lass uns deinen Fall einrichten.“" },
      ],
      try: "Live testen",
    },
    pricing: { title:"Ein Preis pro Unternehmensgröße", subtitle:"Ohne Bindung, ohne Karte für die Probe.", perMonth:"/ Monat", mostPopular:"Am beliebtesten", onQuote:"Auf Anfrage", cta:"Starten", ctaPro:"Pro wählen", ctaPremium:"Kontakt", detected:"Preise für", change:"ändern" },
    finalCta: { title:"24 Stunden lang DIA mit echten Kund:innen erleben.", subtitle:"Keine Karte. Keine Installation. 5 Minuten über WhatsApp.", button:"24h kostenlos starten", tiny:"Keine Kreditkarte" },
    footer: { rights:"Alle Rechte vorbehalten", made:"Für Afrika. Für die Welt." },
    common: { showMore: "Mehr anzeigen", showLess: "Weniger" },
  },
};

/* ────────────────────────────────────────────────────────────────
   Languages (3 onglets)
   ──────────────────────────────────────────────────────────────── */
const HD_LANGUAGES = {
  priority: [
    { code: "FR", name: "Français",    sample: "Bonjour, je peux vous aider ?" },
    { code: "EN", name: "English",     sample: "Hi! How can I help you today?" },
    { code: "AR", name: "العربية",      sample: "مرحبا، كيف يمكنني مساعدتك؟", rtl: true },
    { code: "DAR", name: "Darija",     sample: "Salam, kifach n3awnek ?" },
    { code: "ES", name: "Español",     sample: "Hola, ¿en qué te puedo ayudar?" },
    { code: "PT", name: "Português",   sample: "Olá! Como posso ajudar?" },
  ],
  african: [
    { code: "WO", name: "Wolof",       sample: "Salaam, naka mu may dimbali ?" },
    { code: "LIN", name: "Lingala",    sample: "Mbote ! Na koki kosalisa yo ndenge nini ?" },
    { code: "BAM", name: "Bambara",    sample: "I ni sɔgɔma, ne be se ka i dɛmɛ wa ?" },
    { code: "SW", name: "Kiswahili",   sample: "Habari! Nawezaje kukusaidia?" },
    { code: "YOR", name: "Yoruba",     sample: "Ẹ n lẹ! Báwo ni mo ṣe lè ràn yín lọ́wọ́?" },
    { code: "HAU", name: "Hausa",      sample: "Sannu! Ta yaya zan iya taimaka maka?" },
    { code: "AM", name: "አማርኛ",        sample: "ሰላም! እንዴት ልረዳዎት?" },
    { code: "ZU", name: "isiZulu",     sample: "Sawubona! Ngingakusiza kanjani?" },
    { code: "EWE", name: "Ewe",        sample: "Ŋdi! Nukae mawɔ na wò?" },
    { code: "FON", name: "Fon",        sample: "Aklunɔ! Etɛ̀ wù wa nu we?" },
  ],
  international: [
    { code: "DE", name: "Deutsch",     sample: "Hallo! Wie kann ich helfen?" },
    { code: "IT", name: "Italiano",    sample: "Ciao! Come posso aiutarti?" },
    { code: "NL", name: "Nederlands",  sample: "Hallo! Hoe kan ik helpen?" },
    { code: "TR", name: "Türkçe",      sample: "Merhaba! Nasıl yardımcı olabilirim?" },
    { code: "RU", name: "Русский",     sample: "Здравствуйте! Чем помочь?" },
    { code: "ZH", name: "中文",         sample: "你好!我能帮您什么?" },
    { code: "HI", name: "हिन्दी",        sample: "नमस्ते! मैं कैसे मदद करूँ?" },
    { code: "JA", name: "日本語",        sample: "こんにちは!お手伝いしましょうか?" },
  ],
};

/* ────────────────────────────────────────────────────────────────
   Pricing par pays
   ──────────────────────────────────────────────────────────────── */
const HD_PRICING_BY_REGION = {
  morocco: {
    label: "Maroc", flag: "🇲🇦", currency: "MAD",
    plans: [
      { id:"nano",    name:"Nano",    price:"190",  unit:"MAD", desc:"Démarrer en solo" },
      { id:"starter", name:"Starter", price:"590",  unit:"MAD", desc:"Petite équipe", popular:true },
      { id:"pro",     name:"Pro",     price:"1190", unit:"MAD", desc:"Volume sérieux" },
      { id:"premium", name:"Premium", price:"—",    unit:"",    desc:"Sur mesure" },
    ],
  },
  fcfa: {
    label: "Afrique CFA", flag: "🌍", currency: "FCFA",
    plans: [
      { id:"nano",    name:"Nano",    price:"12 500", unit:"FCFA", altPrice:"19 €",  desc:"Démarrer en solo" },
      { id:"starter", name:"Starter", price:"38 500", unit:"FCFA", altPrice:"59 €",  desc:"Petite équipe", popular:true },
      { id:"pro",     name:"Pro",     price:"78 000", unit:"FCFA", altPrice:"119 €", desc:"Volume sérieux" },
      { id:"premium", name:"Premium", price:"—",      unit:"",     desc:"Sur mesure" },
    ],
  },
  europe: {
    label: "Europe", flag: "🇪🇺", currency: "EUR",
    plans: [
      { id:"nano",    name:"Nano",    price:"19",  unit:"€", desc:"Démarrer en solo" },
      { id:"starter", name:"Starter", price:"59",  unit:"€", desc:"Petite équipe", popular:true },
      { id:"pro",     name:"Pro",     price:"119", unit:"€", desc:"Volume sérieux" },
      { id:"premium", name:"Premium", price:"—",   unit:"",  desc:"Sur mesure" },
    ],
  },
  america: {
    label: "Amérique", flag: "🌎", currency: "USD",
    plans: [
      { id:"nano",    name:"Nano",    price:"21",  unit:"$", desc:"Solo founders" },
      { id:"starter", name:"Starter", price:"65",  unit:"$", desc:"Small team", popular:true },
      { id:"pro",     name:"Pro",     price:"129", unit:"$", desc:"Serious volume" },
      { id:"premium", name:"Premium", price:"—",   unit:"",  desc:"Custom" },
    ],
  },
};

/* Plan features — communs aux pays */
const HD_PLAN_FEATURES = {
  nano: [
    "1 canal connecté (WhatsApp)",
    "500 messages IA / mois",
    "Réponses 6 langues",
    "1 secteur configuré",
  ],
  starter: [
    "2 canaux connectés",
    "2 000 messages IA / mois",
    "Toutes les langues",
    "Relances automatiques",
    "Tableau de bord ventes",
  ],
  pro: [
    "Tous les canaux",
    "10 000 messages IA / mois",
    "Personnalisation avancée",
    "Intégrations (Stripe, Sheets…)",
    "Support prioritaire",
  ],
  premium: [
    "Volume illimité",
    "Modèle IA dédié",
    "Multi-établissements",
    "SLA & onboarding",
    "Account manager",
  ],
};

/* Mapping pays  région tarifaire & langue */
const HD_COUNTRY_TO_REGION = {
  // Maroc
  MA: { region: "morocco",  lang: "fr" },
  // Afrique francophone (CFA)
  SN: { region:"fcfa", lang:"fr" }, CI:{region:"fcfa",lang:"fr"}, BJ:{region:"fcfa",lang:"fr"},
  TG:{region:"fcfa",lang:"fr"}, ML:{region:"fcfa",lang:"fr"}, BF:{region:"fcfa",lang:"fr"},
  NE:{region:"fcfa",lang:"fr"}, CM:{region:"fcfa",lang:"fr"}, GA:{region:"fcfa",lang:"fr"},
  CG:{region:"fcfa",lang:"fr"}, CD:{region:"fcfa",lang:"fr"}, TD:{region:"fcfa",lang:"fr"},
  GN:{region:"fcfa",lang:"fr"}, GW:{region:"fcfa",lang:"pt"}, MG:{region:"fcfa",lang:"fr"},
  // Nigeria, US/Canada  america
  NG: { region: "america", lang: "en" },
  // Anglophone Afrique  CFA region
  GH:{region:"fcfa",lang:"en"}, KE:{region:"fcfa",lang:"en"}, UG:{region:"fcfa",lang:"en"}, ZA:{region:"fcfa",lang:"en"},
  // Pays arabes
  DZ:{region:"morocco",lang:"ar"}, TN:{region:"morocco",lang:"ar"}, EG:{region:"europe",lang:"ar"},
  AE:{region:"europe",lang:"ar"}, SA:{region:"europe",lang:"ar"},
  // Espagne / LatAm
  ES:{region:"europe",lang:"es"}, MX:{region:"america",lang:"es"}, AR:{region:"america",lang:"es"},
  BR:{region:"america",lang:"pt"},
  // Portugal
  PT:{region:"europe",lang:"pt"},
  // Allemagne / pays germanophones
  DE:{region:"europe",lang:"de"}, AT:{region:"europe",lang:"de"}, CH:{region:"europe",lang:"de"},
  // FR / autres EU
  FR:{region:"europe",lang:"fr"}, BE:{region:"europe",lang:"fr"}, LU:{region:"europe",lang:"fr"},
  // Amérique (USD)
  US:{region:"america",lang:"en"}, CA:{region:"america",lang:"en"},
  // UK  europe
  GB:{region:"europe",lang:"en"},
};

/* expose globally for other Babel scripts */
Object.assign(window, {
  LANDING_CONTENT, HD_LANGUAGES,
  HD_PRICING_BY_REGION, HD_PLAN_FEATURES, HD_COUNTRY_TO_REGION,
});

  


/* ────────────────────────────────────────────────────────────────
   DIA — Hero chat demo (4 onglets génériques, sans logos)
   Channels are evoked with color tints & abstract icons.
   ──────────────────────────────────────────────────────────────── */


/* ──── Channel theming (generic, no trademarks) ───────────────── */
const HD_CHANNELS = [
  { id: "messaging", label: "Messaging", tint: "#1FA868", soft: "rgba(31,168,104,.12)",
    icon: ( /* speech bubble */
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
    ),
    status: "actif" },
  { id: "social-comments", label: "Comments", tint: "#3D7BE0", soft: "rgba(61,123,224,.12)",
    icon: ( /* thumbs/heart-style stacked */
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
    ),
    status: "bientôt" },
  { id: "photo-feed", label: "Photo feed", tint: "#D946A6", soft: "rgba(217,70,166,.12)",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="4"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>
    ),
    status: "bientôt" },
  { id: "dms", label: "Private DMs", tint: "#9B5CF6", soft: "rgba(155,92,246,.12)",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13"/><path d="m22 2-7 20-4-9-9-4 20-7z"/></svg>
    ),
    status: "bientôt" },
];

/* ──── Avatar (initial + tinted bg) ──────────────────────────── */
function HDAvatar({ name, bg = "#2a2a2a", size = 28, fg = "#fff" }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.4, fontWeight: 600, color: fg, flexShrink: 0,
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06)",
    }}>{name[0]?.toUpperCase()}</div>
  );
}

/* ──── DIA mini-mark (envelope + dots) ──────────────────── */
function HDMark({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 22 22" aria-hidden>
      <rect x="2" y="4" width="18" height="12" rx="3.5" fill="#fff"/>
      <path d="M8 16 L5 20 L13 16" fill="#fff"/>
      <circle cx="7" cy="10" r="1.4" fill="#C04828"/>
      <circle cx="11" cy="10" r="1.4" fill="#C04828"/>
      <circle cx="15" cy="10" r="1.4" fill="#C04828"/>
    </svg>
  );
}

/* ──── Typing dots ───────────────────────────────────────────── */
function HDTyping({ color = "#888" }) {
  return (
    <div style={{ display: "inline-flex", gap: 3, padding: "6px 10px", background: "#1a1a1a", borderRadius: 12 }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 5, height: 5, borderRadius: "50%", background: color,
          animation: `hd-typing 1.2s ease-in-out ${i * 0.15}s infinite`,
        }} />
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Tab 1 — Messaging (WhatsApp-style, generic green tint)
   ════════════════════════════════════════════════════════════════ */
const HD_MESSAGING_CONVS = [
  { lang: "FR", flag: "🇫🇷", name: "Amélie", topic: "Boutique",
    msgs: [
      { c: "client", t: "Bonjour, vous avez l'abaya en taille M ?" },
      { c: "ai", t: "Bonjour Amélie 👋 Oui, en M et en 4 couleurs : noir, beige, bordeaux, kaki. La rouge bordeaux est la plus demandée 😉" },
      { c: "client", t: "Combien et vous livrez à Lyon ?" },
      { c: "ai", t: "8 000 FCFA, livraison Lyon 24-48h via DHL. Je vous envoie le lien de paiement ?" },
    ] },
  { lang: "Darija", flag: "🇲🇦", name: "Younes", topic: "Hôtel",
    msgs: [
      { c: "client", t: "Salam, kayna chambre l week-end jay ?" },
      { c: "ai", t: "Salam Younes ! Ah, kayna chambre double 590 MAD/nuit, breakfast inclus 🥐. T9dem ched 9bel l'after-midi ?" },
      { c: "client", t: "Wakha, 2 nuits svp." },
      { c: "ai", t: "Mzyan, 1180 MAD t9adi. Kanstanaw confirmation deba w katwsalk QR code ✓" },
    ] },
  { lang: "English", flag: "🇬🇧", name: "Tunde", topic: "Restaurant",
    msgs: [
      { c: "client", t: "Hi, table for 4 tonight 8pm?" },
      { c: "ai", t: "Hey Tunde — yes, we've got a window seat at 8:00 sharp. Any allergies I should flag for the kitchen?" },
      { c: "client", t: "One vegetarian, one nut allergy." },
      { c: "ai", t: "Noted ✓ I'll mark it on your booking. See you at 8!" },
    ] },
  { lang: "Wolof", flag: "🇸🇳", name: "Aïssatou", topic: "Salon",
    msgs: [
      { c: "client", t: "Salaam, dafa am rendez-vous ngir tresses suba ?" },
      { c: "ai", t: "Salaam Aïssatou ! Wow, am na : 10h walla 14h. Kan moo gën ci yaw ?" },
      { c: "client", t: "14h baax na." },
      { c: "ai", t: "Mbaax ✓ Réservation bi confirmé na. Bu ngeen ñëw, indil sa carte." },
    ] },
  { lang: "Lingala", flag: "🇨🇩", name: "Christian", topic: "Boutique",
    msgs: [
      { c: "client", t: "Mbote ! Bo zali na sandales numéro 43 ?" },
      { c: "ai", t: "Mbote Christian ! Ee, tozali na 43 mpe na 44, mitindo mibale 👟. Olingi ya mwindo to ya pɔndu ?" },
      { c: "client", t: "Mwindo. Ntalo nini ?" },
      { c: "ai", t: "15 000 FC. Bokeyi kotinda na Gombe lobi 🚚" },
    ] },
  { lang: "Arabe", flag: "🇸🇦", name: "Layla", topic: "Clinique", rtl: true,
    msgs: [
      { c: "client", t: "السلام، أبغي موعد استشارة أولى." },
      { c: "ai", t: "وعليكم السلام ليلى 🌸. متاح غدا الساعة ١٠ صباحا أو ٤ مساء، أيهما يناسبك؟" },
      { c: "client", t: "١٠ صباحا، شكرا." },
      { c: "ai", t: "تم الحجز ✓ سنرسل لك رسالة تذكير قبل الموعد بساعة." },
    ] },
];

function HDDemoMessaging() {
  const [convIdx, setConvIdx] = useState(0);
  const [visible, setVisible] = useState(0);
  const [typing, setTyping] = useState(false);
  const [progress, setProgress] = useState(0);
  const conv = HD_MESSAGING_CONVS[convIdx];

  /* play conversation */
  useEffect(() => {
    setVisible(0); setTyping(false); setProgress(0);
    const timers = [];
    const total = conv.msgs.length;
    let acc = 350;
    conv.msgs.forEach((m, i) => {
      if (m.c === "ai") {
        timers.push(setTimeout(() => setTyping(true), acc));
        acc += 700;
        timers.push(setTimeout(() => { setTyping(false); setVisible(v => Math.max(v, i + 1)); }, acc));
      } else {
        timers.push(setTimeout(() => setVisible(v => Math.max(v, i + 1)), acc));
      }
      acc += 850;
    });
    /* progress bar */
    const startTime = performance.now();
    const duration = 7800;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - startTime) / duration);
      setProgress(p);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    timers.push(setTimeout(() => setConvIdx(i => (i + 1) % HD_MESSAGING_CONVS.length), duration));
    return () => { timers.forEach(clearTimeout); cancelAnimationFrame(raf); };
  }, [convIdx]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* chat header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
        background: "linear-gradient(180deg,#143d29,#0f2f20)", borderBottom: "1px solid rgba(255,255,255,.04)" }}>
        <HDAvatar name={conv.name} bg="#1FA868" size={32} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: "#fff", fontWeight: 600, lineHeight: 1.2 }}>{conv.name}</div>
          <div style={{ fontSize: 10.5, color: "rgba(255,255,255,.55)" }}>{conv.topic} · en ligne</div>
        </div>
        <div style={{ fontSize: 16 }}>{conv.flag}</div>
        <div style={{ padding: "3px 7px", borderRadius: 999, fontSize: 9.5, fontWeight: 600,
          background: "rgba(255,255,255,.08)", color: "#fff", letterSpacing: ".4px" }}>{conv.lang}</div>
      </div>

      {/* progress bar */}
      <div style={{ height: 2, background: "rgba(255,255,255,.04)" }}>
        <div style={{ height: "100%", width: `${progress * 100}%`, background: "linear-gradient(90deg,#C04828,#FF6B3D)", transition: "width .12s linear" }} />
      </div>

      {/* messages */}
      <div style={{
        flex: 1, overflow: "hidden", padding: "16px 14px", display: "flex",
        flexDirection: "column", gap: 8,
        background: "radial-gradient(circle at 50% 0%, rgba(31,168,104,.06), transparent 70%), #0d1612",
        direction: conv.rtl ? "rtl" : "ltr",
      }}>
        {conv.msgs.slice(0, visible).map((m, i) => (
          <div key={`${convIdx}-${i}`} style={{
            display: "flex", justifyContent: m.c === "ai" ? "flex-end" : "flex-start",
            animation: "hd-fade-up .35s ease both",
          }}>
            <div style={{
              maxWidth: "78%",
              padding: "8px 11px",
              borderRadius: m.c === "ai" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
              background: m.c === "ai" ? "linear-gradient(180deg,#1FA868,#178a55)" : "#1f1f1f",
              color: "#fff",
              fontSize: 12.5,
              lineHeight: 1.45,
              boxShadow: m.c === "ai" ? "0 4px 14px rgba(31,168,104,.18)" : "0 2px 6px rgba(0,0,0,.2)",
              direction: conv.rtl ? "rtl" : "ltr",
            }}>
              {m.c === "ai" && (
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4, opacity: .85, fontSize: 9.5, fontWeight: 600, letterSpacing: ".3px" }}>
                  <HDMark size={10} /> DIA
                </div>
              )}
              {m.t}
            </div>
          </div>
        ))}
        {typing && (
          <div style={{ display: "flex", justifyContent: "flex-end", animation: "hd-fade-up .25s ease both" }}>
            <HDTyping color="#1FA868" />
          </div>
        )}
      </div>

      {/* footer pills cycling languages */}
      <div style={{ display: "flex", gap: 6, padding: "10px 14px", borderTop: "1px solid rgba(255,255,255,.04)",
        background: "#0a120e", overflow: "hidden" }}>
        {HD_MESSAGING_CONVS.map((c, i) => (
          <div key={c.lang} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i === convIdx ? "linear-gradient(90deg,#C04828,#FF6B3D)" : "rgba(255,255,255,.07)",
            transition: "background .3s",
          }} />
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Tab 2 — Comments under a social post (Facebook-style, generic)
   ════════════════════════════════════════════════════════════════ */
const HD_FB_COMMENTS = [
  { name: "Mariam D.", lang: "FR",  t: "C'est dispo en taille L ? 😊", reply: "Bonjour Mariam ! Oui, le L est en stock — couleur noir et beige. Je vous DM le lien ?" },
  { name: "Khadim N.", lang: "Wolof", t: "Combien la livraison à Thiès ?", reply: "Salaam Khadim ! Livraison Thiès na 1 500 FCFA, 24-48h ✓ Bo bëgg, ma yónnee link bi." },
  { name: "Aïcha B.",  lang: "FR",  t: "Vous faites des paiements à la livraison ?", reply: "Oui Aïcha ! Paiement à la livraison disponible à Dakar et Thiès, sans frais supplémentaires 🙌" },
  { name: "Boris K.",  lang: "FR",  t: "Bonsoir, vos horaires demain ?", reply: "Bonsoir Boris ! Demain on est ouvert 9h-19h, non-stop. À très vite 👋" },
];

function HDDemoComments() {
  const [revealed, setRevealed] = useState(0);
  const [typingIdx, setTypingIdx] = useState(-1);

  useEffect(() => {
    setRevealed(0); setTypingIdx(-1);
    const timers = [];
    let acc = 600;
    HD_FB_COMMENTS.forEach((_, i) => {
      timers.push(setTimeout(() => setRevealed(r => Math.max(r, i + 1)), acc));
      acc += 1100;
      timers.push(setTimeout(() => setTypingIdx(i), acc));
      acc += 800;
      timers.push(setTimeout(() => { setTypingIdx(-1); setRevealed(r => Math.max(r, (i + 1) * 10)); }, acc));
      acc += 700;
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#0e1419", overflow: "hidden" }}>
      {/* fake post header */}
      <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,.05)",
        background: "#121a21" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#C04828,#FF6B3D)",
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <HDMark size={14} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: "#fff", fontWeight: 600 }}>Boutique Aurora</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)" }}>il y a 2h · 🌍 Public</div>
          </div>
          <div style={{ padding: "3px 8px", borderRadius: 6, fontSize: 9.5, fontWeight: 600,
            background: "rgba(61,123,224,.18)", color: "#7db0ff", letterSpacing: ".4px" }}>POST</div>
        </div>
        <div style={{ marginTop: 10, fontSize: 12.5, color: "rgba(255,255,255,.85)", lineHeight: 1.5 }}>
          Nouvelle collection « Sahel » 🌅 — disponible dès maintenant. DM ouverts ✨
        </div>
        {/* image placeholder */}
        <div style={{ marginTop: 10, height: 70, borderRadius: 10,
          background: "repeating-linear-gradient(45deg, rgba(192,72,40,.18), rgba(192,72,40,.18) 8px, rgba(192,72,40,.10) 8px, rgba(192,72,40,.10) 16px)",
          border: "1px solid rgba(255,255,255,.05)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: '"JetBrains Mono", monospace', fontSize: 9.5, color: "rgba(255,255,255,.4)",
          letterSpacing: ".5px",
        }}>
          [ photo produit ]
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 14, fontSize: 10.5, color: "rgba(255,255,255,.55)" }}>
          <span>♡ 124</span><span>💬 {Math.min(revealed, HD_FB_COMMENTS.length) * 2}</span><span>↗ 12</span>
        </div>
      </div>

      {/* comments stream */}
      <div style={{ flex: 1, overflow: "hidden", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
        {HD_FB_COMMENTS.map((c, i) => (
          revealed > i && (
            <div key={i} style={{ animation: "hd-fade-up .35s ease both" }}>
              {/* user comment */}
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <HDAvatar name={c.name} bg="#3D7BE0" size={24} />
                <div style={{ background: "#1c2530", borderRadius: 12, padding: "6px 10px", maxWidth: "82%" }}>
                  <div style={{ fontSize: 10.5, color: "#fff", fontWeight: 600, marginBottom: 2 }}>{c.name}</div>
                  <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.85)", lineHeight: 1.4 }}>{c.t}</div>
                </div>
              </div>
              {/* AI typing */}
              {typingIdx === i && (
                <div style={{ display: "flex", gap: 8, marginTop: 6, paddingLeft: 32 }}>
                  <HDTyping color="#FF6B3D" />
                </div>
              )}
              {/* AI reply */}
              {(revealed > (i + 1) * 10 - 1 || revealed >= (i + 1) * 10) && (
                <div style={{ display: "flex", gap: 8, marginTop: 6, paddingLeft: 32, animation: "hd-fade-up .3s ease both" }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: "linear-gradient(135deg,#C04828,#FF6B3D)",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <HDMark size={10} />
                  </div>
                  <div style={{ background: "linear-gradient(180deg,rgba(192,72,40,.18),rgba(192,72,40,.10))",
                    border: "1px solid rgba(192,72,40,.35)", borderRadius: 12, padding: "6px 10px", maxWidth: "82%" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                      <span style={{ fontSize: 10.5, color: "#fff", fontWeight: 600 }}>Boutique Aurora</span>
                      <span style={{ fontSize: 8.5, padding: "1px 5px", borderRadius: 4,
                        background: "rgba(255,107,61,.2)", color: "#FF9D78", fontWeight: 600, letterSpacing: ".3px" }}>DIA</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.9)", lineHeight: 1.4 }}>{c.reply}</div>
                  </div>
                </div>
              )}
            </div>
          )
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Tab 3 — Photo feed comments (IG-style, generic)
   ════════════════════════════════════════════════════════════════ */
const HD_IG_COMMENTS = [
  { name: "salma_o", lang: "Darija", t: "Wach kayn lik f mqyas 38 ?", reply: "Salam @salma_o 💕 ah, kayn 38 w 39, lon noir w bordeaux. Lina nsiftek lien deba?" },
  { name: "marie.k",  lang: "FR",     t: "C'est de la soie ? trop belle 😍", reply: "Merci @marie.k ! Oui, soie naturelle 100% ✨ DM pour les options de couleur." },
  { name: "yacine_w", lang: "Darija", t: "Chhal taman ?", reply: "Hi @yacine_w ! 890 MAD, livraison majjanan f Casa 🛵 nsiftek details f DM ?" },
];

function HDDemoPhotoFeed() {
  const [revealed, setRevealed] = useState(0);
  const [replyIdx, setReplyIdx] = useState(-1);

  useEffect(() => {
    setRevealed(0); setReplyIdx(-1);
    const timers = [];
    let acc = 500;
    HD_IG_COMMENTS.forEach((_, i) => {
      timers.push(setTimeout(() => setRevealed(r => r + 1), acc)); acc += 900;
      timers.push(setTimeout(() => setReplyIdx(i), acc)); acc += 1100;
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#000", overflow: "hidden" }}>
      {/* photo card */}
      <div style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%",
          background: "conic-gradient(from 0deg,#FF6B3D,#D946A6,#9B5CF6,#FF6B3D)", padding: 1.5 }}>
          <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: "#000",
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <HDMark size={11} />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11.5, color: "#fff", fontWeight: 600 }}>boutique.aurora</div>
          <div style={{ fontSize: 9.5, color: "rgba(255,255,255,.5)" }}>Casablanca, Maroc</div>
        </div>
        <div style={{ fontSize: 14, color: "#fff" }}>···</div>
      </div>
      {/* photo placeholder */}
      <div style={{ aspectRatio: "1.3 / 1", margin: "0 0",
        background: "linear-gradient(135deg,#2a1a1a,#1a1a2a), repeating-linear-gradient(45deg, rgba(217,70,166,.18), rgba(217,70,166,.18) 10px, transparent 10px, transparent 20px)",
        backgroundBlendMode: "overlay",
        position: "relative", overflow: "hidden",
        borderBottom: "1px solid rgba(255,255,255,.05)",
      }}>
        <div style={{ position: "absolute", inset: 0,
          background: "repeating-linear-gradient(45deg, rgba(255,255,255,.04), rgba(255,255,255,.04) 10px, transparent 10px, transparent 22px)" }} />
        <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
          fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: "rgba(255,255,255,.5)",
          letterSpacing: ".5px", padding: "5px 10px", border: "1px dashed rgba(255,255,255,.15)", borderRadius: 4 }}>
          [ photo produit ]
        </div>
      </div>
      {/* actions */}
      <div style={{ padding: "8px 12px 4px", display: "flex", gap: 12, alignItems: "center", color: "#fff" }}>
        <span style={{ fontSize: 16 }}>♡</span>
        <span style={{ fontSize: 14 }}>💬</span>
        <span style={{ fontSize: 14 }}>↗</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 14, opacity: .7 }}>⌐</span>
      </div>
      <div style={{ padding: "0 12px 6px", fontSize: 10.5, color: "#fff", fontWeight: 600 }}>
        {1284 + revealed} mentions j'aime
      </div>

      {/* comments */}
      <div style={{ flex: 1, overflow: "hidden", padding: "0 12px 10px", display: "flex", flexDirection: "column", gap: 7 }}>
        {HD_IG_COMMENTS.map((c, i) => (
          revealed > i && (
            <div key={i} style={{ animation: "hd-fade-up .3s ease both" }}>
              <div style={{ display: "flex", gap: 7, alignItems: "flex-start" }}>
                <HDAvatar name={c.name} bg="#D946A6" size={20} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: "#fff", lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 600, marginRight: 6 }}>{c.name}</span>
                    <span style={{ color: "rgba(255,255,255,.85)" }}>{c.t}</span>
                  </div>
                  {replyIdx >= i && (
                    <div style={{ display: "flex", gap: 6, marginTop: 5, animation: "hd-fade-up .3s ease both" }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: "linear-gradient(135deg,#C04828,#FF6B3D)",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <HDMark size={9} />
                      </div>
                      <div style={{ fontSize: 10.5, color: "rgba(255,255,255,.75)", lineHeight: 1.4 }}>
                        <span style={{ color: "#fff", fontWeight: 600, marginRight: 5 }}>boutique.aurora</span>
                        <span style={{ display: "inline-block", padding: "0 4px", borderRadius: 3,
                          background: "rgba(255,107,61,.2)", color: "#FF9D78", fontWeight: 600,
                          fontSize: 8, letterSpacing: ".3px", marginRight: 5 }}>DIA</span>
                        {c.reply}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Tab 4 — Private DMs (IG-style dark, generic)
   ════════════════════════════════════════════════════════════════ */
const HD_DM = [
  { c: "client", t: "Hi ! J'ai vu votre robe verte sur le post" },
  { c: "ai", t: "Salam ! Merci 🌿 La verte « Atlas », elle est en S, M et L. Quelle taille ?" },
  { c: "client", t: "M. Et ça coûte combien wakha ?" },
  { c: "ai", t: "1 290 MAD ✨ avec livraison gratuite à Casa et Rabat. Je te génère le lien de paiement ?" },
  { c: "client", t: "Yes go !" },
  { c: "ai", t: "✓ Lien envoyé. Tu reçois la robe en 24h ⚡" },
];

function HDDemoDM() {
  const [visible, setVisible] = useState(0);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    setVisible(0); setTyping(false);
    const timers = [];
    let acc = 350;
    HD_DM.forEach((m, i) => {
      if (m.c === "ai") {
        timers.push(setTimeout(() => setTyping(true), acc)); acc += 700;
        timers.push(setTimeout(() => { setTyping(false); setVisible(v => v + 1); }, acc)); acc += 750;
      } else {
        timers.push(setTimeout(() => setVisible(v => v + 1), acc)); acc += 850;
      }
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%",
      background: "linear-gradient(180deg,#1a0d1f 0%, #0c0710 100%)" }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
        borderBottom: "1px solid rgba(255,255,255,.05)" }}>
        <div style={{ fontSize: 18, color: "#fff", marginRight: 4 }}>‹</div>
        <div style={{ width: 30, height: 30, borderRadius: "50%",
          background: "conic-gradient(from 0deg,#FF6B3D,#D946A6,#9B5CF6,#FF6B3D)", padding: 1.5 }}>
          <HDAvatar name="K" bg="#1a0d1f" size={27} fg="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: "#fff", fontWeight: 600 }}>karim_dz</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)" }}>Actif il y a 2 min</div>
        </div>
        <div style={{ padding: "3px 8px", borderRadius: 6, fontSize: 9.5, fontWeight: 600,
          background: "rgba(155,92,246,.2)", color: "#c4a3ff", letterSpacing: ".4px" }}>DM</div>
      </div>

      {/* messages */}
      <div style={{ flex: 1, overflow: "hidden", padding: "16px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        {HD_DM.slice(0, visible).map((m, i) => (
          <div key={i} style={{
            display: "flex", justifyContent: m.c === "ai" ? "flex-end" : "flex-start",
            animation: "hd-fade-up .35s ease both",
          }}>
            <div style={{
              maxWidth: "80%", padding: "8px 12px",
              borderRadius: m.c === "ai" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
              background: m.c === "ai"
                ? "linear-gradient(135deg,#C04828,#FF6B3D)"
                : "rgba(255,255,255,.07)",
              color: "#fff", fontSize: 12.5, lineHeight: 1.45,
              boxShadow: m.c === "ai" ? "0 4px 14px rgba(192,72,40,.22)" : "none",
              border: m.c === "client" ? "1px solid rgba(255,255,255,.06)" : "none",
            }}>
              {m.c === "ai" && (
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3, opacity: .9, fontSize: 9.5, fontWeight: 600, letterSpacing: ".3px" }}>
                  <HDMark size={10} /> DIA
                </div>
              )}
              {m.t}
            </div>
          </div>
        ))}
        {typing && (
          <div style={{ display: "flex", justifyContent: "flex-end", animation: "hd-fade-up .25s ease both" }}>
            <HDTyping color="#FF6B3D" />
          </div>
        )}
      </div>

      {/* fake input */}
      <div style={{ padding: "10px 12px", borderTop: "1px solid rgba(255,255,255,.05)" }}>
        <div style={{ height: 32, borderRadius: 16, border: "1px solid rgba(255,255,255,.08)",
          display: "flex", alignItems: "center", paddingLeft: 12, fontSize: 11, color: "rgba(255,255,255,.35)" }}>
          Message…
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Tabbed shell
   ════════════════════════════════════════════════════════════════ */
function HDChatDemo({ height = 520 }) {
  const [tab, setTab] = useState(0);

  /* auto-rotate tabs */
  useEffect(() => {
    const iv = setInterval(() => setTab(t => (t + 1) % HD_CHANNELS.length), 12000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div style={{
      width: "100%", maxWidth: 460,
      borderRadius: 24, padding: 1,
      background: "linear-gradient(180deg,rgba(255,255,255,.10),rgba(255,255,255,.02))",
      boxShadow: "0 30px 80px -20px rgba(192,72,40,.35), 0 0 0 1px rgba(255,255,255,.04)",
      position: "relative",
    }}>
      {/* glow blob behind */}
      <div aria-hidden style={{ position: "absolute", inset: -40, zIndex: -1,
        background: "radial-gradient(50% 50% at 50% 50%, rgba(192,72,40,.35), transparent 70%)",
        filter: "blur(40px)", animation: "hd-blob 8s ease-in-out infinite" }} />

      <div style={{ borderRadius: 23, background: "#0a0a0a", overflow: "hidden",
        display: "flex", flexDirection: "column", height: height }}>
        {/* tab bar */}
        <div style={{ display: "flex", padding: 6, gap: 4, background: "#0f0f0f",
          borderBottom: "1px solid rgba(255,255,255,.04)" }}>
          {HD_CHANNELS.map((c, i) => {
            const active = i === tab;
            return (
              <button key={c.id} onClick={() => setTab(i)} style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "8px 6px", borderRadius: 10, border: "none", cursor: "pointer",
                background: active ? c.soft : "transparent",
                color: active ? c.tint : "rgba(255,255,255,.5)",
                fontSize: 11, fontWeight: 600,
                transition: "all .25s ease",
                position: "relative",
                overflow: "hidden",
              }}>
                <span style={{ display: "inline-flex" }}>{c.icon}</span>
                <span className="hd-tab-label">{c.label}</span>
                {i === 0 && active && (
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#1FA868",
                    boxShadow: "0 0 0 3px rgba(31,168,104,.25)", animation: "hd-pulse-dot 1.6s ease-in-out infinite" }} />
                )}
              </button>
            );
          })}
        </div>

        {/* status strip */}
        <div style={{ padding: "6px 14px", display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#0d0d0d", borderBottom: "1px solid rgba(255,255,255,.04)",
          fontSize: 10, color: "rgba(255,255,255,.45)", letterSpacing: ".4px", textTransform: "uppercase" }}>
          <span>{tab === 0 ? "Auto-réponse messagerie" : tab === 1 ? "Modération commentaires" : tab === 2 ? "Réponses photo feed" : "DMs privés"}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: tab === 0 ? "#1FA868" : "#FF9D78" }} />
            {tab === 0 ? "live" : "preview"}
          </span>
        </div>

        {/* body */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          {tab === 0 && <HDDemoMessaging />}
          {tab === 1 && <HDDemoComments />}
          {tab === 2 && <HDDemoPhotoFeed />}
          {tab === 3 && <HDDemoDM />}
        </div>
      </div>

      <style>{`
        @media (max-width: 560px) {
          .hd-tab-label { display: none; }
        }
      `}</style>
    </div>
  );
}

window.HDChatDemo = HDChatDemo;
window.HDMark = HDMark;
window.HD_CHANNELS = HD_CHANNELS;

  



/* ────────────────────────────────────────────────────────────────
   DIA CRM — Landing app (Nav · Hero · Languages · Sectors ·
   Pricing · FinalCTA · Footer · Tweaks panel)
   ──────────────────────────────────────────────────────────────── */

const useStateL = useState;
const useEffectL = useEffect;
const useMemoL = useCallback;
const useRefL = useRef;

/* ════════════════════════════════════════════════════════════════
   Tweak defaults — direct-edit persistable
   ════════════════════════════════════════════════════════════════ */
const HD_TWEAKS = /*EDITMODE-BEGIN*/{
  "lang": "fr",
  "region": "auto",
  "accent": "#C04828",
  "accent2": "#FF6B3D",
  "headlineSize": 72,
  "showStats": true,
  "showLanguages": true,
  "showSectors": true,
  "showPricing": true,
  "navStyle": "glass"
}/*EDITMODE-END*/;

/* ════════════════════════════════════════════════════════════════
   useIsMobile hook
   ════════════════════════════════════════════════════════════════ */
function useIsMobile(breakpoint = 720) {
  const [mobile, setMobile] = useStateL(() =>
    typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffectL(() => {
    const check = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", check, { passive: true });
    check(); // re-check immédiatement au montage
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);
  return mobile;
}

/* ════════════════════════════════════════════════════════════════
   Logo mark — envelope + 3 dots (matches the favicon thumbnail)
   ════════════════════════════════════════════════════════════════ */
function HDLogo({ size = 28, accent = "#C04828" }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.28,
      background: "linear-gradient(135deg,#1a1a1a,#000)",
      border: "1px solid rgba(255,255,255,.08)",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    }}>
      <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 22 22">
        <rect x="2" y="4" width="18" height="12" rx="3" fill="#fff"/>
        <path d="M8 16 L5 20 L13 16" fill="#fff"/>
        <circle cx="7" cy="10" r="1.4" fill={accent}/>
        <circle cx="11" cy="10" r="1.4" fill={accent}/>
        <circle cx="15" cy="10" r="1.4" fill={accent}/>
      </svg>
    </div>
  );
}

function HDLogoLockup({ accent }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <HDLogo size={30} accent={accent} />
      <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-.01em", color: "#f5f5f5" }}>
        Hellodia
        <span style={{ color: accent, marginLeft: 4 }}>.</span>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Nav
   ════════════════════════════════════════════════════════════════ */
function HDNav({ t, accent, lang, setLang, navStyle, goToRegister }) {
  const [scrolled, setScrolled] = useStateL(false);
  useEffectL(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const langOptions = [
    { code: "fr", flag: "🇫🇷", label: "FR", name: "Français" },
    { code: "en", flag: "🇬🇧", label: "EN", name: "English" },
    { code: "ar", flag: "🇸🇦", label: "AR", name: "العربية" },
    { code: "es", flag: "🇪🇸", label: "ES", name: "Español" },
    { code: "pt", flag: "🇵🇹", label: "PT", name: "Português" },
    { code: "de", flag: "🇩🇪", label: "DE", name: "Deutsch" },
  ];

  const isGlass = navStyle === "glass";
  const navBg = scrolled
    ? (isGlass ? "rgba(10,10,10,.72)" : "#0a0a0a")
    : "transparent";

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 40,
      padding: "14px 5vw",
      background: navBg,
      backdropFilter: scrolled && isGlass ? "blur(18px) saturate(180%)" : "none",
      WebkitBackdropFilter: scrolled && isGlass ? "blur(18px) saturate(180%)" : "none",
      borderBottom: scrolled ? "1px solid rgba(255,255,255,.06)" : "1px solid transparent",
      transition: "all .25s ease",
    }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", alignItems: "center", gap: 16 }}>
        <HDLogoLockup accent={accent} />

        <div style={{ flex: 1 }} />

        {/* lang dropdown */}
        <HDLangDropdown lang={lang} setLang={setLang} options={langOptions} />

        <a href="#" onClick={(e) => { e.preventDefault(); goToRegister(); }} className="hd-nav-login" style={{
          fontSize: 13, color: "rgba(255,255,255,.7)", padding: "8px 14px",
          borderRadius: 10, transition: "color .15s",
        }}
          onMouseEnter={e => e.currentTarget.style.color = "#fff"}
          onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,.7)"}
        >
          {t.nav.login}
        </a>


      </div>

    </nav>
  );
}

/* ════════════════════════════════════════════════════════════════
   Language dropdown (proper select)
   ════════════════════════════════════════════════════════════════ */
function HDLangDropdown({ lang, setLang, options }) {
  const [open, setOpen] = useStateL(false);
  const ref = useRefL(null);
  const current = options.find(o => o.code === lang) || options[0];

  useEffectL(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "7px 11px 7px 10px", borderRadius: 10,
        background: open ? "rgba(255,255,255,.08)" : "rgba(255,255,255,.04)",
        border: "1px solid rgba(255,255,255,.08)",
        color: "#fff", fontSize: 12, fontWeight: 600, letterSpacing: ".3px",
        cursor: "pointer", transition: "all .15s",
      }}>
        <span style={{ fontSize: 14, lineHeight: 1 }}>{current.flag}</span>
        <span>{current.label}</span>
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform .2s", opacity: .6,
        }}>
          <polyline points="3 4.5 6 7.5 9 4.5" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0,
          minWidth: 170, padding: 4,
          background: "rgba(15,15,15,.96)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          border: "1px solid rgba(255,255,255,.1)",
          borderRadius: 12,
          boxShadow: "0 12px 40px rgba(0,0,0,.5)",
          animation: "hd-fade-up .15s ease both",
          zIndex: 50,
        }}>
          {options.map(o => {
            const active = o.code === lang;
            return (
              <button key={o.code} onClick={() => { setLang(o.code); setOpen(false); }} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10,
                padding: "9px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                background: active ? "rgba(255,255,255,.08)" : "transparent",
                color: active ? "#fff" : "rgba(255,255,255,.7)",
                fontSize: 13, fontWeight: 500, textAlign: "left",
                transition: "background .12s",
              }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,.04)"; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>{o.flag}</span>
                <span style={{ flex: 1 }}>{o.name || o.label}</span>
                {active && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="2.5 6 5 8.5 9.5 3.5"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Rotating word component
   ════════════════════════════════════════════════════════════════ */
function HDRotate({ words, accent, accent2, interval = 2400 }) {
  const [i, setI] = useStateL(0);
  useEffectL(() => {
    const iv = setInterval(() => setI(x => (x + 1) % words.length), interval);
    return () => clearInterval(iv);
  }, [words.length, interval]);
  return (
    <span style={{ position: "relative", display: "inline-block", verticalAlign: "baseline" }}>
      <span aria-hidden style={{ visibility: "hidden", display: "inline-flex", flexDirection: "column" }}>
        {words.map((w, idx) => (
          <span key={idx} style={{ height: 0, overflow: "hidden", whiteSpace: "normal" }}>{w}</span>
        ))}
        <span style={{ whiteSpace: "normal" }}>
          {words.reduce((a, b) => (a.length >= b.length ? a : b), "")}
        </span>
      </span>
      <span key={i} style={{
        position: "absolute", left: 0, top: 0, right: 0, whiteSpace: "normal",
        background: `linear-gradient(135deg,${accent},${accent2})`,
        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        backgroundClip: "text", color: "transparent",
        animation: "hd-rot-in .55s cubic-bezier(.22,.61,.36,1) both",
      }}>{words[i]}</span>
      <style>{`
        @keyframes hd-rot-in {
          0%   { opacity: 0; transform: translateY(14px); filter: blur(4px); }
          60%  { opacity: 1; filter: blur(0); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
      `}</style>
    </span>
  );
}

/* HDRotatePair — les 2 lignes partagent UN SEUL timer  toujours synchronisées */
function HDRotatePair({ rotate1, rotate2, accent, accent2, interval = 3000 }) {
  const [i, setI] = useStateL(0);
  useEffectL(() => {
    const iv = setInterval(() => setI(x => (x + 1) % rotate1.length), interval);
    return () => clearInterval(iv);
  }, [rotate1.length, interval]);

  const AnimWord = ({ words, grad }) => (
  <span key={i} style={{
    background: grad,
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    backgroundClip: "text", color: "transparent",
    animation: "hd-rot-in .55s cubic-bezier(.22,.61,.36,1) both",
  }}>{words[i]}</span>
  );

  return (
  <>
    <span style={{ display: "block", marginBottom: "-0.15em" }}>
      <AnimWord words={rotate1} grad={`linear-gradient(135deg,${accent},${accent2})`} />
    </span>
    <span style={{ display: "block" }}>
      <AnimWord words={rotate2} grad={`linear-gradient(135deg,${accent2},${accent})`} />
    </span>
  </>
);
}

/* ════════════════════════════════════════════════════════════════
   Channel chips above hero
   ════════════════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════════════════
   DIA LOADER — animation de chargement avec la mascotte
   ════════════════════════════════════════════════════════════════ */
const DIA_C = {
  sandLight:"#FFE4B8", sandMid:"#E8C18A", cream:"#FFF1DC",
  coral:"#FF8C5C", coralDeep:"#C04828", blush:"#FFB199",
  ink:"#1a0d08",
};
let _diaCounter = 0;
function diaSvgInline(mood, size) {
  size = size || 160;
  const id = ++_diaCounter;
  const H = `dh-${id}`, E = `de-${id}`, C = DIA_C;
  const baseEl = `<ellipse cx="240" cy="430" rx="125" ry="14" fill="rgba(0,0,0,.32)"/>
    <g transform="translate(165 175) rotate(-20)"><ellipse cx="0" cy="0" rx="48" ry="92" fill="url(#${H})"/><ellipse cx="0" cy="22" rx="26" ry="64" fill="url(#${E})" opacity="0.92"/></g>
    <g transform="translate(315 175) rotate(20)"><ellipse cx="0" cy="0" rx="48" ry="92" fill="url(#${H})"/><ellipse cx="0" cy="22" rx="26" ry="64" fill="url(#${E})" opacity="0.92"/></g>
    <ellipse cx="240" cy="282" rx="128" ry="115" fill="url(#${H})"/>
    <ellipse cx="240" cy="322" rx="88" ry="62" fill="${C.cream}"/>
    <ellipse cx="158" cy="320" rx="20" ry="11" fill="${C.blush}" opacity="0.7"/>
    <ellipse cx="322" cy="320" rx="20" ry="11" fill="${C.blush}" opacity="0.7"/>
    <path d="M 232 308 Q 240 300 248 308 Q 248 318 240 322 Q 232 318 232 308 Z" fill="${C.ink}"/>
    <path d="M 178 332 L 145 330" stroke="rgba(0,0,0,.32)" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M 178 340 L 142 346" stroke="rgba(0,0,0,.28)" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M 302 332 L 335 330" stroke="rgba(0,0,0,.32)" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M 302 340 L 338 346" stroke="rgba(0,0,0,.28)" stroke-width="1.5" stroke-linecap="round"/>`;
  const M = {
    thinking: {
      eyes:`<ellipse cx="200" cy="272" rx="13" ry="17" fill="${C.ink}"/><ellipse cx="280" cy="272" rx="13" ry="17" fill="${C.ink}"/><circle cx="206" cy="265" r="4.5" fill="#fff"/><circle cx="286" cy="265" r="4.5" fill="#fff"/><path d="M 262 244 Q 280 232 298 244" stroke="${C.ink}" stroke-width="4.5" stroke-linecap="round" fill="none"/>`,
      mouth:`<ellipse cx="240" cy="338" rx="7" ry="4" fill="${C.ink}"/>`,
    },
    typing: {
      eyes:`<ellipse cx="200" cy="272" rx="12" ry="10" fill="${C.ink}"/><ellipse cx="280" cy="272" rx="12" ry="10" fill="${C.ink}"/><circle cx="203" cy="269" r="3" fill="#fff"/><circle cx="283" cy="269" r="3" fill="#fff"/>`,
      mouth:`<path d="M 220 334 Q 240 352 260 334" stroke="${C.ink}" stroke-width="4" stroke-linecap="round" fill="none"/>`,
    },
    joyful: {
      eyes:`<path d="M 182 272 Q 200 252 218 272" stroke="${C.ink}" stroke-width="6" stroke-linecap="round" fill="none"/><path d="M 262 272 Q 280 252 298 272" stroke="${C.ink}" stroke-width="6" stroke-linecap="round" fill="none"/>`,
      mouth:`<path d="M 205 332 Q 240 380 275 332 Q 270 358 240 362 Q 210 358 205 332 Z" fill="${C.ink}"/><path d="M 218 350 Q 240 374 262 350" fill="${C.coral}"/>`,
    },
    happy: {
      eyes:`<ellipse cx="200" cy="270" rx="13" ry="17" fill="${C.ink}"/><ellipse cx="280" cy="270" rx="13" ry="17" fill="${C.ink}"/><circle cx="204" cy="263" r="4.5" fill="#fff"/><circle cx="284" cy="263" r="4.5" fill="#fff"/>`,
      mouth:`<path d="M 215 333 Q 240 360 265 333 Q 263 354 240 358 Q 217 354 215 333 Z" fill="${C.ink}"/>`,
    },
  };
  const m = M[mood] || M.happy;
  return `<svg viewBox="0 0 480 480" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="${H}" cx="50%" cy="40%" r="65%"><stop offset="0%" stop-color="${C.sandLight}"/><stop offset="100%" stop-color="${C.sandMid}"/></radialGradient><radialGradient id="${E}" cx="50%" cy="60%" r="60%"><stop offset="0%" stop-color="${C.coral}"/><stop offset="100%" stop-color="${C.coralDeep}"/></radialGradient></defs>${baseEl}${m.eyes}${m.mouth}</svg>`;
}

function TypewriterText({ text }) {
  const [displayed, setDisplayed] = useState("");
  const [diaStart] = useState(text.indexOf("DIA"));
  const [phase, setPhase] = useState("typing"); // typing → erasing → retyping
  const [cursor, setCursor] = useState(true);

  // Clignotement curseur
  useEffect(() => {
    const t = setInterval(() => setCursor(c => !c), 530);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let i = 0;
    let timer;

    if (phase === "typing") {
      timer = setInterval(() => {
        if (i >= text.length) {
          clearInterval(timer);
          setTimeout(() => setPhase("erasing"), 600);
          return;
        }
        setDisplayed(text.slice(0, i + 1));
        i++;
      }, 75);

    } else if (phase === "erasing") {
      i = text.length;
      timer = setInterval(() => {
        if (i <= diaStart) {
          clearInterval(timer);
          setTimeout(() => setPhase("retyping"), 200);
          return;
        }
        setDisplayed(text.slice(0, i - 1));
        i--;
      }, 60);

    } else if (phase === "retyping") {
      i = diaStart;
      timer = setInterval(() => {
        if (i >= text.length) { clearInterval(timer); return; }
        setDisplayed(text.slice(0, i + 1));
        i++;
      }, 90);
    }

    return () => clearInterval(timer);
  }, [phase]);

  const beforeDia = displayed.slice(0, diaStart);
  const diaTyped = displayed.slice(diaStart);
  const isRetyping = phase === "retyping";

  return (
    <span style={{ fontSize:22, fontWeight:700, letterSpacing:"-.02em", fontFamily:"Inter,ui-sans-serif,sans-serif" }}>
      <span style={{ color:"#f5f5f5" }}>{beforeDia}</span>
      <span style={{ color: isRetyping ? "#C04828" : "#f5f5f5" }}>{diaTyped}</span>
      <span style={{
        color:"#C04828", fontWeight:300,
        opacity: cursor ? 1 : 0,
        transition: "opacity 0.1s",
      }}>|</span>
    </span>
  );
}

function DIALoader({ onDone }) {
  const [phase, setPhase] = useState(0);
  const [bounce, setBounce] = useState(false);
  const [nameVisible, setNameVisible] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const phases = [
    { mood: "thinking", dur: 1100 },
    { mood: "typing",   dur: 1000 },
    { mood: "joyful",   dur: 1200, bounce: true },
    { mood: "happy",    dur: 900 },
  ];
  useEffect(() => {
    let timer;
    let current = 0;
    setTimeout(() => setNameVisible(true), 400);
    function next() {
      if (current >= phases.length) {
        setFadeOut(true);
        setTimeout(() => onDone && onDone(), 500);
        return;
      }
      const p = phases[current];
      setPhase(current);
      setBounce(!!p.bounce);
      timer = setTimeout(() => { setBounce(false); current++; next(); }, p.dur);
    }
    next();
    return () => clearTimeout(timer);
  }, []);
  const currentMood = phases[phase]?.mood || "happy";
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999, background:"#0a0a0a",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:20,
      opacity: fadeOut ? 0 : 1, transition:"opacity 0.5s ease",
      pointerEvents: fadeOut ? "none" : "all",
    }}>
      <div style={{
        transform: bounce ? "translateY(-18px) scale(1.08)" : "translateY(0) scale(1)",
        transition: bounce ? "transform 0.25s cubic-bezier(.22,.61,.36,1.4)" : "transform 0.35s cubic-bezier(.22,.61,.36,1)",
      }} dangerouslySetInnerHTML={{ __html: diaSvgInline(currentMood, 160) }} />
      <div style={{
        display:"flex", alignItems:"center", gap:6,
        opacity: nameVisible ? 1 : 0,
        transform: nameVisible ? "translateY(0)" : "translateY(8px)",
        transition:"opacity 0.4s ease, transform 0.4s ease",
      }}>
        <TypewriterText text="Hello je suis DIA" />
      </div>
    </div>
  );
}

function HDChannelChips({ t }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px 6px 8px",
      borderRadius: 999,
      background: "rgba(31,168,104,.08)",
      border: "1px solid rgba(31,168,104,.2)",
      fontSize: 12, color: "rgba(255,255,255,.85)", fontWeight: 500,
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: "50%", background: "#1FA868",
        boxShadow: "0 0 0 4px rgba(31,168,104,.18)",
        animation: "hd-pulse-dot 1.6s ease-in-out infinite",
      }} />
      {t.hero.online}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Hero
   ════════════════════════════════════════════════════════════════ */
function StatItem({ s, i, isMobile }) {
  const [displayed, setDisplayed] = useState(s.v);
  useEffect(() => {
    if (!isMobile) return;
    const num = parseFloat(s.v.replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return;
    const prefix = s.v.match(/^[^0-9]*/)?.[0] || "";
    const suffix = s.v.match(/[^0-9.]+$/)?.[0] || "";
    let start = 0;
    const steps = 40;
    const increment = num / steps;
    const timer = setInterval(() => {
      start += increment;
      if (start >= num) { setDisplayed(s.v); clearInterval(timer); }
      else setDisplayed(`${prefix}${Math.floor(start)}${suffix}`);
    }, 1500 / steps);
    return () => clearInterval(timer);
  }, [isMobile, s.v]);

  return (
    <div style={{
      borderLeft: i === 0 ? "none" : "1px solid rgba(255,255,255,.06)",
      padding: i === 0 ? "0" : "0 16px",
      paddingLeft: i === 0 ? 0 : 18,
    }}>
      <div style={{
        fontSize: 24, fontWeight: 600, color: "#fff",
        letterSpacing: "-.02em",
        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      }}>{isMobile ? displayed : s.v}</div>
      <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.5)", marginTop: 4, letterSpacing: ".02em" }}>
        {s.l}
      </div>
    </div>
  );
}

function HDHero({ t, accent, accent2, headlineSize, showStats, isMobile, goToRegister, onDemo, lang }) {
  return (
    <section style={{
      position: "relative",
      padding: isMobile ? "96px 5vw 48px" : "88px 5vw 40px",
      overflow: "hidden",
      minHeight: "auto",
    }}>
      {/* ambient blobs */}
      <div className="hd-hero-blobs" aria-hidden style={{
        position: "absolute", top: "10%", left: "-15%", width: 500, height: 500,
        borderRadius: "50%", background: accent, filter: "blur(120px)",
        opacity: .18, animation: "hd-blob 14s ease-in-out infinite",
      }} />
      <div className="hd-hero-blobs" aria-hidden style={{
        position: "absolute", bottom: "-10%", right: "-10%", width: 600, height: 600,
        borderRadius: "50%", background: accent2, filter: "blur(140px)",
        opacity: .12, animation: "hd-blob 18s ease-in-out infinite reverse",
      }} />
      {/* grid pattern */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, opacity: .35,
        backgroundImage: "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at 50% 30%, black 30%, transparent 75%)",
        WebkitMaskImage: "radial-gradient(ellipse at 50% 30%, black 30%, transparent 75%)",
      }} />

      <div style={{
        position: "relative", zIndex: 2,
        maxWidth: 1280, margin: "0 auto",
        display: "grid",
        gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 480px)",
        gap: isMobile ? 32 : 64,
        alignItems: "center",
        textAlign: isMobile ? "center" : "left",
      }} className="hd-hero-grid">

        {/* LEFT */}
        <div style={{
          display: "flex", flexDirection: "column", gap: isMobile ? 62 : 18,
          alignItems: isMobile ? "center" : "stretch",
        }}>
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? "clamp(34px, 11vw, 34px)" : `clamp(28px, ${headlineSize / 22}vw + 4px, ${headlineSize*0.8}px)`,
            lineHeight: 1.1, letterSpacing: "-.025em", fontWeight: 600,
            color: "#fff", textWrap: "balance",
            display: "block",
          }}>
            <HDRotatePair rotate1={t.hero.rotate1} rotate2={t.hero.rotate2} accent={accent} accent2={accent2} interval={3000} />
            <span style={{ color: "rgba(255,255,255,.55)", fontWeight: 400, whiteSpace: "nowrap" }}>{t.hero.rotate3}</span>
          </h1>

          <p style={{
            margin: 0, fontSize: 17, lineHeight: 1.6, color: "rgba(255,255,255,.7)",
            maxWidth: 520,
          }}>
            {lang === "fr" ? <><span style={{
              background: "linear-gradient(135deg,#C04828,#FF6B3D)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              backgroundClip: "text", fontWeight: 700,
              filter: "drop-shadow(0 0 8px rgba(192,72,40,.6))",
            }}>DIA</span> répond à vos clients sur <span style={{color:"#25D366",fontWeight:600}}>WhatsApp</span>, dans les commentaires <span style={{color:"#1877F2",fontWeight:600}}>Facebook</span> et <span style={{background:"linear-gradient(90deg,#E1306C,#833AB4)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",fontWeight:600}}>Instagram</span>.</> :
             lang === "en" ? <>DIA replies, convinces and converts your customers in their language — on <span style={{color:"#25D366",fontWeight:600}}>WhatsApp</span>, in <span style={{color:"#1877F2",fontWeight:600}}>Facebook</span> &amp; <span style={{background:"linear-gradient(90deg,#E1306C,#833AB4)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",fontWeight:600}}>Instagram</span> comments, and in DMs.</> :
             lang === "ar" ? <>هيلوديا يرد ويُقنع ويحوّل عملاءك بلغتهم — على <span style={{color:"#25D366",fontWeight:600}}>واتساب</span>، في تعليقات <span style={{color:"#1877F2",fontWeight:600}}>فيسبوك</span> و<span style={{background:"linear-gradient(90deg,#E1306C,#833AB4)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",fontWeight:600}}>إنستغرام</span>، وفي الرسائل الخاصة.</> :
             lang === "es" ? <>DIA responde, convence y convierte a tus clientes en su idioma — en <span style={{color:"#25D366",fontWeight:600}}>WhatsApp</span>, en comentarios de <span style={{color:"#1877F2",fontWeight:600}}>Facebook</span> e <span style={{background:"linear-gradient(90deg,#E1306C,#833AB4)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",fontWeight:600}}>Instagram</span>, y en DMs.</> :
             lang === "pt" ? <>DIA responde, convence e converte os seus clientes no seu idioma — no <span style={{color:"#25D366",fontWeight:600}}>WhatsApp</span>, nos comentários do <span style={{color:"#1877F2",fontWeight:600}}>Facebook</span> e <span style={{background:"linear-gradient(90deg,#E1306C,#833AB4)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",fontWeight:600}}>Instagram</span>, e em DMs.</> :
             lang === "de" ? <>DIA antwortet, überzeugt und konvertiert Ihre Kunden in ihrer Sprache — auf <span style={{color:"#25D366",fontWeight:600}}>WhatsApp</span>, in <span style={{color:"#1877F2",fontWeight:600}}>Facebook</span>- und <span style={{background:"linear-gradient(90deg,#E1306C,#833AB4)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",fontWeight:600}}>Instagram</span>-Kommentaren und in DMs.</> :
             t.hero.subtitle}
          </p>

          {/* CTA row */}
          <div style={{
            display: "flex", flexWrap: isMobile ? "nowrap" : "wrap", gap: isMobile ? 8 : 14,
            alignItems: "center",
            justifyContent: isMobile ? "center" : "flex-start",
          }}>
            <button onClick={goToRegister} style={{
              padding: isMobile ? "11px 14px" : "14px 22px", borderRadius: 12,
              background: `linear-gradient(135deg,${accent},${accent2})`,
              color: "#fff", fontSize: isMobile ? 13 : 15, fontWeight: 600, border: "none",
              boxShadow: `0 10px 30px -8px ${accent}88`,
              display: "inline-flex", alignItems: "center", gap: isMobile ? 6 : 10,
              cursor: "pointer", flexShrink: 0,
              transition: "transform .15s, box-shadow .15s",
              animation: "hd-glow 2.4s ease-in-out infinite",
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}
            >
              {t.hero.cta}
              <span style={{ fontSize: 18 }}></span>
            </button>
            <button onClick={() => onDemo && onDemo("auto")} style={{
              padding: isMobile ? "11px 12px" : "14px 18px", borderRadius: 12,
              border: "1px solid rgba(255,255,255,.12)",
              fontSize: isMobile ? 13 : 14, color: "rgba(255,255,255,.85)", fontWeight: 500,
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "transparent", cursor: "pointer", flexShrink: 0,
              transition: "all .15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.2)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(255,255,255,.12)"; }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 2, border: `2px solid ${accent2}`, borderRightColor: "transparent", borderBottomColor: "transparent", transform: "rotate(45deg)", display: "inline-block" }} />
              Voir une démo
            </button>
          </div>

          {/* stats strip */}
          {showStats && (
            <div className="hd-hero-stats" style={{
              marginTop: 12,
              display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
              gap: 0,
              borderTop: "1px solid rgba(255,255,255,.06)",
              paddingTop: 24,
            }}>
              {t.hero.stats.map((s, i) => (
                <StatItem key={i} s={s} i={i} isMobile={isMobile} />
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — chat demo (desktop only) */}
        {!isMobile && (
          <div className="hd-chat-wrap" style={{ display: "flex", justifyContent: "center", animation: "hd-float 6s ease-in-out infinite" }}>
            <HDChatDemo height={480} />
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 980px) {
          .hd-hero-grid { grid-template-columns: minmax(0, 1fr) !important; gap: 36px !important; }
        }
        @media (max-width: 720px) {
          .hd-hero-grid { gap: 28px !important; }
        }
      `}</style>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════
   Languages section
   ════════════════════════════════════════════════════════════════ */
function HDLanguagesSection({ t, accent, accent2, isMobile }) {
  const [tab, setTab] = useStateL("priority");
  const [expanded, setExpanded] = useStateL(false);
  const list = HD_LANGUAGES[tab];
  const visibleList = isMobile && !expanded ? list.slice(0, 3) : list;
  const hiddenCount = list.length - 3;
  const moreLabel = (t.common && t.common.showMore) || "Voir plus";
  const lessLabel = (t.common && t.common.showLess) || "Voir moins";

  /* reset expanded when tab changes */
  useEffectL(() => { setExpanded(false); }, [tab]);

  return (
    <section style={{ padding: "56px 5vw 64px", position: "relative" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ fontSize: 11, color: accent2, letterSpacing: ".25em", fontWeight: 600, marginBottom: 14, textTransform: "uppercase" }}>
            50+ langues
          </div>
          <h2 style={{
            margin: 0, fontSize: "clamp(28px,3.4vw,46px)", lineHeight: 1.1,
            letterSpacing: "-.02em", fontWeight: 600, color: "#fff",
            textWrap: "balance",
          }}>{t.languages.title}</h2>
          <p style={{ margin: "16px auto 0", maxWidth: 560, color: "rgba(255,255,255,.6)", fontSize: 16, lineHeight: 1.6 }}>
            {t.languages.subtitle}
          </p>
        </div>

        {/* tabs */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
          <div style={{
            display: "inline-flex", padding: 4, borderRadius: 12,
            background: "rgba(255,255,255,.03)",
            border: "1px solid rgba(255,255,255,.06)",
          }}>
            {Object.entries(t.languages.tabs).map(([k, v]) => (
              <button key={k} onClick={() => setTab(k)} style={{
                padding: "10px 18px", borderRadius: 8, border: "none", cursor: "pointer",
                background: tab === k ? `linear-gradient(135deg,${accent},${accent2})` : "transparent",
                color: tab === k ? "#fff" : "rgba(255,255,255,.55)",
                fontSize: 13, fontWeight: 600,
                boxShadow: tab === k ? `0 4px 14px -4px ${accent}AA` : "none",
                transition: "all .2s",
              }}>{v}</button>
            ))}
          </div>
        </div>

        {/* grid */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
        }}>
          {visibleList.map((l, i) => (
            <div key={l.code} style={{
              padding: 16,
              borderRadius: 12,
              background: "rgba(255,255,255,.025)",
              border: "1px solid rgba(255,255,255,.06)",
              transition: "all .2s",
              cursor: "default",
              animation: `hd-fade-up .4s ease both ${i * 30}ms`,
              direction: l.rtl ? "rtl" : "ltr",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.05)"; e.currentTarget.style.borderColor = `${accent2}40`; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.025)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.06)"; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, direction: "ltr" }}>
                <div style={{ fontSize: 14, color: "#fff", fontWeight: 600 }}>{l.name}</div>
                <span style={{
                  fontSize: 9.5, padding: "2px 6px", borderRadius: 4,
                  background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.5)",
                  fontFamily: '"JetBrains Mono", monospace', letterSpacing: ".5px",
                }}>{l.code}</span>
              </div>
              <div style={{ fontSize: 12.5, color: "rgba(255,255,255,.65)", lineHeight: 1.5, fontStyle: "italic" }}>
                « {l.sample} »
              </div>
            </div>
          ))}
        </div>

        {/* Voir plus toggle (mobile only) */}
        {isMobile && hiddenCount > 0 && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
            <button onClick={() => setExpanded(e => !e)} style={{
              padding: "10px 20px", borderRadius: 999,
              border: `1px solid ${accent2}40`,
              background: "rgba(255,255,255,.03)",
              color: accent2,
              fontSize: 13, fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 8,
              cursor: "pointer", transition: "all .15s",
            }}>
              {expanded ? lessLabel : `${moreLabel} (+${hiddenCount})`}
              <span style={{
                display: "inline-block",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform .2s",
              }}>↓</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════
   Sectors section
   ════════════════════════════════════════════════════════════════ */
const SECTOR_ICONS = {
  hotel:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V8l9-5 9 5v13"/><path d="M9 21v-7h6v7"/><path d="M3 21h18"/></svg>,
  restaurant: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v7a3 3 0 006 0V2"/><path d="M6 9v13"/><path d="M18 2c-2 0-3 2-3 5s1 5 3 5h0v10"/></svg>,
  boutique:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9h18l-2 12H5L3 9z"/><path d="M8 9V6a4 4 0 018 0v3"/></svg>,
  salon:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="18" r="3"/><circle cx="18" cy="18" r="3"/><path d="M8 18h8"/><path d="M6 18 20 4"/><path d="M18 18 4 4"/></svg>,
  clinique:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20" /><circle cx="12" cy="12" r="9"/></svg>,
  autre:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>,
};

function HDSectorsSection({ t, accent, accent2, isMobile, onPopup }) {
  const [hovered, setHovered] = useStateL(null);
  const [expanded, setExpanded] = useStateL(false);
  const items = t.sectors.items;
  const visibleItems = isMobile && !expanded ? items.slice(0, 3) : items;
  const hiddenCount = items.length - 3;
  const moreLabel = (t.common && t.common.showMore) || "Voir plus";
  const lessLabel = (t.common && t.common.showLess) || "Voir moins";

  return (
    <section id="sectors" style={{
      padding: "56px 5vw 64px",
      background: "linear-gradient(180deg, transparent, rgba(255,255,255,.015), transparent)",
      position: "relative",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div style={{ fontSize: 11, color: accent2, letterSpacing: ".25em", fontWeight: 600, marginBottom: 14, textTransform: "uppercase" }}>
            6 scénarios
          </div>
          <h2 style={{
            margin: 0, fontSize: "clamp(28px,3.4vw,46px)", lineHeight: 1.1,
            letterSpacing: "-.02em", fontWeight: 600, color: "#fff",
          }}>{t.sectors.title}</h2>
          <p style={{ margin: "16px auto 0", maxWidth: 560, color: "rgba(255,255,255,.6)", fontSize: 16, lineHeight: 1.6 }}>
            {t.sectors.subtitle}
          </p>
        </div>

        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 14,
        }}>
          {visibleItems.map((s, i) => {
            const isHovered = hovered === s.id;
            return (
              <div key={s.id}
                onMouseEnter={() => setHovered(s.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  position: "relative", overflow: "hidden",
                  padding: 22, borderRadius: 16,
                  background: isHovered
                    ? `linear-gradient(135deg, rgba(192,72,40,.10), rgba(255,107,61,.06))`
                    : "rgba(255,255,255,.025)",
                  border: `1px solid ${isHovered ? `${accent2}50` : "rgba(255,255,255,.06)"}`,
                  cursor: "pointer",
                  transition: "all .25s ease",
                  transform: isHovered ? "translateY(-4px)" : "translateY(0)",
                  animation: `hd-fade-up .4s ease both ${i * 60}ms`,
                }}>

                {/* shimmer accent on hover */}
                {isHovered && (
                  <div style={{
                    position: "absolute", inset: 0, opacity: .5,
                    background: `radial-gradient(circle at 80% 0%, ${accent}30, transparent 50%)`,
                    pointerEvents: "none",
                  }} />
                )}

                <div style={{ position: "relative", zIndex: 1 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: isHovered
                      ? `linear-gradient(135deg, ${accent}, ${accent2})`
                      : "rgba(255,255,255,.04)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: isHovered ? "#fff" : "rgba(255,255,255,.55)",
                    transition: "all .25s",
                    marginBottom: 16,
                    boxShadow: isHovered ? `0 8px 22px -6px ${accent}AA` : "none",
                  }}>
                    <span style={{ width: 22, height: 22, display: "block" }}>
                      {SECTOR_ICONS[s.id]}
                    </span>
                  </div>

                  <div style={{ fontSize: 17, fontWeight: 600, color: "#fff", marginBottom: 8, letterSpacing: "-.01em" }}>
                    {s.name}
                  </div>
                  <div style={{
                    fontSize: 13, color: "rgba(255,255,255,.6)",
                    lineHeight: 1.5, fontStyle: "italic", marginBottom: 16,
                  }}>
                    {s.ex}
                  </div>

                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    fontSize: 12, fontWeight: 600,
                    color: isHovered ? accent2 : "rgba(255,255,255,.4)",
                    transition: "all .2s",
                  }}>
                    {t.sectors.try}
                    <span style={{ transform: isHovered ? "translateX(3px)" : "translateX(0)", transition: "transform .2s", display: "inline-block" }}></span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Voir plus toggle (mobile only) */}
        {isMobile && hiddenCount > 0 && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
            <button onClick={() => setExpanded(e => !e)} style={{
              padding: "10px 20px", borderRadius: 999,
              border: `1px solid ${accent2}40`,
              background: "rgba(255,255,255,.03)",
              color: accent2,
              fontSize: 13, fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 8,
              cursor: "pointer", transition: "all .15s",
            }}>
              {expanded ? lessLabel : `${moreLabel} (+${hiddenCount})`}
              <span style={{
                display: "inline-block",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform .2s",
              }}>↓</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════
   Pricing section
   ════════════════════════════════════════════════════════════════ */

   const HD_PRICING_USD = {
  nano:    { usd: 21 },
  starter: { usd: 65 },
  pro:     { usd: 129 },
  premium: { usd: null },
};

function HDPricingSection({ t, accent, accent2, region, setRegion, goToRegister, onContact }) {
const reg = HD_PRICING_BY_REGION[region] || HD_PRICING_BY_REGION.morocco;

const [rates, setRates] = useState({});
const [localCurrency, setLocalCurrency] = useState(null);

useEffect(() => {
  const currencyMap = {
    MA:{code:"MAD",symbol:"MAD"},
    SN:{code:"XOF",symbol:"FCFA"}, CI:{code:"XOF",symbol:"FCFA"},
    ML:{code:"XOF",symbol:"FCFA"}, BF:{code:"XOF",symbol:"FCFA"},
    CM:{code:"XAF",symbol:"FCFA"}, GA:{code:"XAF",symbol:"FCFA"},
    NG:{code:"NGN",symbol:"₦"}, GH:{code:"GHS",symbol:"GH₵"},
    FR:{code:"EUR",symbol:"€"}, DE:{code:"EUR",symbol:"€"},
    ES:{code:"EUR",symbol:"€"}, IT:{code:"EUR",symbol:"€"},
    BE:{code:"EUR",symbol:"€"}, PT:{code:"EUR",symbol:"€"},
    GB:{code:"GBP",symbol:"£"},
    US:{code:"USD",symbol:"$"}, CA:{code:"CAD",symbol:"CA$"},
    DZ:{code:"DZD",symbol:"DA"}, TN:{code:"TND",symbol:"DT"},
    EG:{code:"EGP",symbol:"EGP"}, SA:{code:"SAR",symbol:"SAR"},
    AE:{code:"AED",symbol:"AED"},
    BR:{code:"BRL",symbol:"R$"},
  };

  // Géolocalisation par IP — gratuit, sans clé
  fetch("https://ipapi.co/json/")
    .then(r => r.json())
    .then(data => {
      const country = data.country_code || "";
      const detected = currencyMap[country] || { code: "EUR", symbol: "€" };
      setLocalCurrency(detected);
      if (detected.code !== "USD") {
        return fetch("https://open.er-api.com/v6/latest/USD")
          .then(r => r.json())
          .then(data => setRates(data.rates || {}));
      }
    })
    .catch(() => {
      // Fallback si l'API échoue
      setLocalCurrency({ code: "EUR", symbol: "€" });
    });
}, []);

  return (
    <section id="pricing" style={{ padding: "56px 5vw 64px", position: "relative" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 11, color: accent2, letterSpacing: ".25em", fontWeight: 600, marginBottom: 14, textTransform: "uppercase" }}>
            Tarifs
          </div>
          <h2 style={{
            margin: 0, fontSize: "clamp(28px,3.4vw,46px)", lineHeight: 1.1,
            letterSpacing: "-.02em", fontWeight: 600, color: "#fff",
          }}>{t.pricing.title}</h2>
          <p style={{ margin: "16px auto 0", maxWidth: 520, color: "rgba(255,255,255,.6)", fontSize: 16, lineHeight: 1.6 }}>
            {t.pricing.subtitle}
          </p>
        </div>

        {/* plans */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 16,
        }}>
          {reg.plans.map((p, i) => {
            const features = HD_PLAN_FEATURES[p.id];
            const isPopular = p.popular;
            const isPremium = p.id === "premium";

            return (
              <div key={p.id} style={{
                position: "relative",
                padding: isPopular ? 1 : 0,
                borderRadius: 18,
                background: isPopular
                  ? `linear-gradient(180deg,${accent},${accent2})`
                  : "transparent",
              }}>
                <div style={{
                  padding: "24px 20px",
                  borderRadius: isPopular ? 17 : 18,
                  background: isPopular ? "#0e0e0e" : "rgba(255,255,255,.025)",
                  border: isPopular ? "none" : "1px solid rgba(255,255,255,.06)",
                  height: "100%",
                  display: "flex", flexDirection: "column", gap: 16,
                  transition: "all .2s",
                  position: "relative",
                  overflow: "hidden",
                }}>
                  {isPopular && (
                    <div style={{
                      position: "absolute", top: 12, right: 12,
                      padding: "4px 10px", borderRadius: 999,
                      background: `linear-gradient(135deg,${accent},${accent2})`,
                      fontSize: 10, fontWeight: 700, color: "#fff",
                      letterSpacing: ".5px", textTransform: "uppercase",
                    }}>{t.pricing.mostPopular}</div>
                  )}

                  <div>
                    <div style={{
                      fontSize: 12, fontWeight: 600, color: isPopular ? accent2 : "rgba(255,255,255,.5)",
                      letterSpacing: ".15em", textTransform: "uppercase", marginBottom: 8,
                    }}>{p.name}</div>
                    <div style={{ fontSize: 13, color: "rgba(255,255,255,.6)", marginBottom: 14 }}>
                      {p.desc}
                    </div>

                    <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
                      <span style={{
                      fontSize: 38, fontWeight: 600, color: "#fff",
                      letterSpacing: "-.02em",
                      fontFamily: isPremium ? "inherit" : '"JetBrains Mono", ui-monospace, monospace',
                    }}>
                      {isPremium ? t.pricing.onQuote : `$${HD_PRICING_USD[p.id]?.usd}`}
                    </span>
                    {!isPremium && (
                      <span style={{ fontSize: 14, color: "rgba(255,255,255,.5)", fontWeight: 500 }}>
                        / mois
                      </span>
                    )}
                    {!isPremium && localCurrency && localCurrency.code !== "USD" && rates[localCurrency.code] && (
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,.35)", marginTop: 4 }}>
                        ≈ {Math.round(HD_PRICING_USD[p.id]?.usd * rates[localCurrency.code])} {localCurrency.symbol}
                      </div>
                    )}
                    </div>
                    {!isPremium && (
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,.4)" }}>
                        {t.pricing.perMonth}{p.altPrice ? ` · ≈ ${p.altPrice}` : ""}
                      </div>
                    )}
                  </div>

                  <div style={{ height: 1, background: "rgba(255,255,255,.06)" }} />

                  <ul style={{
                    margin: 0, padding: 0, listStyle: "none",
                    display: "flex", flexDirection: "column", gap: 10, flex: 1,
                  }}>
                    {features.map((f, j) => (
                      <li key={j} style={{
                        fontSize: 13, color: "rgba(255,255,255,.78)",
                        display: "flex", alignItems: "flex-start", gap: 10, lineHeight: 1.4,
                      }}>
                        <span style={{
                          width: 16, height: 16, borderRadius: 999,
                          background: isPopular ? `${accent2}20` : "rgba(255,255,255,.06)",
                          color: isPopular ? accent2 : "rgba(255,255,255,.6)",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0, marginTop: 2,
                        }}>
                          <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="2 6 5 9 10 3"/></svg>
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <a href="#cta" style={{
                    padding: "12px 18px", borderRadius: 10, textAlign: "center",
                    background: isPopular
                      ? `linear-gradient(135deg,${accent},${accent2})`
                      : (isPremium ? "transparent" : "rgba(255,255,255,.06)"),
                    border: isPremium ? "1px solid rgba(255,255,255,.15)" : "none",
                    color: "#fff", fontSize: 13, fontWeight: 600,
                    boxShadow: isPopular ? `0 8px 22px -6px ${accent}AA` : "none",
                    transition: "all .15s",
                  }}
                    onMouseEnter={e => { if (!isPopular) e.currentTarget.style.background = isPremium ? "rgba(255,255,255,.04)" : "rgba(255,255,255,.1)"; }}
                    onMouseLeave={e => { if (!isPopular) e.currentTarget.style.background = isPremium ? "transparent" : "rgba(255,255,255,.06)"; }}
                  >
                    {isPremium ? t.pricing.ctaPremium : (isPopular ? t.pricing.ctaPro : t.pricing.cta)}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════
   Final CTA
   ════════════════════════════════════════════════════════════════ */
function HDFinalCTA({ t, accent, accent2, goToRegister }) {
  return (
      <section id="cta" className="hd-cta-section" style={{ padding: "72px 5vw 88px", position: "relative", overflow: "hidden" }}>
      {/* dramatic glow */}
      <div aria-hidden style={{
        position: "absolute", left: "50%", top: "50%",
        transform: "translate(-50%,-50%)",
        width: 900, height: 900, borderRadius: "50%",
        background: `radial-gradient(closest-side, ${accent}25, transparent 70%)`,
        filter: "blur(40px)", pointerEvents: "none",
      }} />

      <div className="hd-cta-card" style={{
        position: "relative", zIndex: 1,
        maxWidth: 820, margin: "0 auto", textAlign: "center",
        padding: "48px 24px",
        borderRadius: 24,
        background: "linear-gradient(180deg, rgba(255,255,255,.04), rgba(255,255,255,.01))",
        border: "1px solid rgba(255,255,255,.08)",
        boxShadow: `0 30px 80px -20px ${accent}30`,
      }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: `linear-gradient(135deg,${accent},${accent2})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 12px 28px -6px ${accent}AA`,
          }}>
            <HDMark size={26} />
          </div>
        </div>

        <h2 style={{
          margin: 0, fontSize: "clamp(28px,3.6vw,46px)", lineHeight: 1.1,
          letterSpacing: "-.02em", fontWeight: 600, color: "#fff",
          textWrap: "balance",
        }}>{t.finalCta.title}</h2>

        <p style={{
          margin: "20px auto 0", maxWidth: 520,
          color: "rgba(255,255,255,.65)", fontSize: 16, lineHeight: 1.6,
        }}>{t.finalCta.subtitle}</p>

        <div style={{ marginTop: 32, display: "inline-flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          <a href="#" style={{
            padding: "16px 28px", borderRadius: 12,
            background: `linear-gradient(135deg,${accent},${accent2})`,
            color: "#fff", fontSize: 16, fontWeight: 600,
            boxShadow: `0 14px 36px -8px ${accent}AA`,
            display: "inline-flex", alignItems: "center", gap: 12,
            animation: "hd-glow 2.4s ease-in-out infinite",
            transition: "transform .15s",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}
          >
            {t.finalCta.button}
            <span style={{ fontSize: 18 }}></span>
          </a>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.45)" }}>
            {t.finalCta.tiny}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════
   Footer
   ════════════════════════════════════════════════════════════════ */
function HDFooter({ t, accent }) {
  const socials = [
    { name: "Facebook", href: "https://facebook.com/useDIA", color: "#1877F2",
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
    { name: "Instagram", href: "https://instagram.com/useDIA", color: "#E1306C",
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg> },
    { name: "YouTube", href: "https://youtube.com/@useDIA", color: "#FF0000",
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg> },
    { name: "LinkedIn", href: "https://linkedin.com/company/useDIA", color: "#0A66C2",
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
  ];

  return (
    <footer style={{ padding: "48px 5vw 32px", borderTop: "1px solid rgba(255,255,255,.06)", background: "rgba(0,0,0,0.3)" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 40, justifyContent: "space-between", marginBottom: 32 }}>
          {/* Logo + tagline */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <HDLogoLockup accent={accent} />
            <p style={{ margin: 0, fontSize: 13, color: "rgba(255,255,255,.4)", maxWidth: 220, lineHeight: 1.6 }}>
              {t.footer.made}
            </p>
          </div>
          {/* Liens légaux */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 4 }}>Legal</div>
            {[
              { label: "Privacy Policy", href: "/privacy.html" },
              { label: "Terms of Service", href: "/terms.html" },
              { label: "About", href: "/about.html" },
            ].map(l => (
              <a key={l.label} href={l.href}
                style={{ fontSize: 13, color: "rgba(255,255,255,.45)", textDecoration: "none", transition: "color .2s" }}
                onMouseEnter={e => e.target.style.color = "#fff"}
                onMouseLeave={e => e.target.style.color = "rgba(255,255,255,.45)"}>
                {l.label}
              </a>
            ))}
          </div>
          {/* Réseaux sociaux */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 4 }}>Suivez-nous</div>
            <div style={{ display: "flex", gap: 8 }}>
              {socials.map(s => (
                <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" title={s.name}
                  style={{ width: 36, height: 36, borderRadius: 10,
                    background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.08)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "rgba(255,255,255,.5)", textDecoration: "none", transition: "all .2s",
                    flexShrink: 0 }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = `${s.color}22`;
                    e.currentTarget.style.borderColor = `${s.color}55`;
                    e.currentTarget.style.color = s.color;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "rgba(255,255,255,.06)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,.08)";
                    e.currentTarget.style.color = "rgba(255,255,255,.5)";
                  }}>
                  {s.icon}
                </a>
              ))}
            </div>
          </div>
        </div>
        {/* Bottom */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,.06)", paddingTop: 20,
          display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,.3)" }}>© 2026 LIYEL LLC · {t.footer.rights}</span>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,.25)" }}>usehellodia.com</span>
        </div>
      </div>
    </footer>
  );
}



function ContactPopup({ accent, accent2, onClose }) {
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.75)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px",animation:"hd-fade-up 0.3s ease"}}>
      <div style={{background:"#111",borderRadius:"20px",border:"0.5px solid rgba(255,255,255,0.1)",width:"100%",maxWidth:"420px",overflow:"hidden",boxShadow:"0 40px 80px rgba(0,0,0,0.6)"}}>
        {/* Header */}
        <div style={{background:"#161616",padding:"16px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"0.5px solid rgba(255,255,255,0.06)"}}>
          <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
            <div style={{width:"36px",height:"36px",borderRadius:"10px",background:`linear-gradient(135deg,${accent},${accent2})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </div>
            <div>
              <div style={{fontSize:"14px",fontWeight:"600",color:"#fff"}}>Nous contacter</div>
              <div style={{fontSize:"11px",color:"rgba(255,255,255,0.4)"}}>Réponse en moins de 2h</div>
            </div>
          </div>
          <button onClick={onClose} style={{width:"28px",height:"28px",borderRadius:"8px",background:"rgba(255,255,255,0.06)",border:"none",color:"rgba(255,255,255,0.5)",cursor:"pointer",fontSize:"18px",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
        </div>
        {/* Body */}
        <div style={{padding:"24px 20px",display:"flex",flexDirection:"column",gap:"12px"}}>
          <p style={{margin:0,fontSize:"13px",color:"rgba(255,255,255,0.55)",lineHeight:"1.6"}}>
            Une question sur nos offres Enterprise ou Premium ? Notre équipe vous répond rapidement.
          </p>
          {/* WhatsApp */}
          <a href="https://wa.me/12025550123?text=Bonjour%2C%20je%20souhaite%20en%20savoir%20plus%20sur%20DIA%20Premium" target="_blank" rel="noopener noreferrer"
            style={{display:"flex",alignItems:"center",gap:"14px",padding:"16px 18px",background:"rgba(37,211,102,0.08)",border:"1px solid rgba(37,211,102,0.2)",borderRadius:"14px",textDecoration:"none",transition:"all .2s"}}>
            <div style={{width:"40px",height:"40px",borderRadius:"12px",background:"#25D366",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:"13px",fontWeight:"600",color:"#fff",marginBottom:"2px"}}>WhatsApp</div>
              <div style={{fontSize:"11px",color:"rgba(255,255,255,0.45)"}}>Discuter avec notre équipe</div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
          {/* Email */}
          <a href="mailto:hello@useDIA.com?subject=Renseignements%20offre%20Premium"
            style={{display:"flex",alignItems:"center",gap:"14px",padding:"16px 18px",background:"rgba(192,72,40,0.08)",border:"1px solid rgba(192,72,40,0.2)",borderRadius:"14px",textDecoration:"none",transition:"all .2s"}}>
            <div style={{width:"40px",height:"40px",borderRadius:"12px",background:`linear-gradient(135deg,${accent},${accent2})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:"13px",fontWeight:"600",color:"#fff",marginBottom:"2px"}}>Email</div>
              <div style={{fontSize:"11px",color:"rgba(255,255,255,0.45)"}}>hello@useDIA.com</div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
          <div style={{fontSize:"11px",color:"rgba(255,255,255,0.25)",textAlign:"center",marginTop:"4px"}}>
            Disponible 7j/7 · Réponse garantie en moins de 2h ouvrées
          </div>
        </div>
      </div>
    </div>
  );
}



function detectRegionAndLang() {
  try {
    const locale = (navigator.language || "fr-FR");
    const country = (locale.split("-")[1] || "").toUpperCase();
    const map = HD_COUNTRY_TO_REGION[country];
    if (map) return map;
    const lang2 = locale.slice(0, 2).toLowerCase();
    if (["fr","en","ar","es","pt","de"].includes(lang2)) return { region: "europe", lang: lang2 };
  } catch (e) {}
  return { region: "morocco", lang: "fr" };
}

/* ════════════════════════════════════════════════════════════════
   Tweaks panel content
   ════════════════════════════════════════════════════════════════ */
function HDTweaks({ t, setTweak }) {
  return (
    <TweaksPanel>
      <TweakSection label="Langue & région" />
      <TweakSelect label="Langue" value={t.lang}
        options={[
          { value: "fr", label: "Français" },
          { value: "en", label: "English" },
          { value: "ar", label: "العربية" },
          { value: "es", label: "Español" },
          { value: "pt", label: "Português" },
          { value: "de", label: "Deutsch" },
        ]}
        onChange={(v) => setTweak("lang", v)} />
      <TweakSelect label="Région tarifaire" value={t.region}
        options={[
          { value: "auto", label: "Auto-détection" },
          { value: "morocco", label: "🇲🇦 Maroc (MAD)" },
          { value: "fcfa", label: "🌍 Afrique CFA" },
          { value: "europe", label: "🇪🇺 Europe (EUR)" },
          { value: "america", label: "🌎 Amérique (USD)" },
        ]}
        onChange={(v) => setTweak("region", v)} />

      <TweakSection label="Marque" />
      <TweakColor label="Accent principal" value={t.accent}
        onChange={(v) => setTweak("accent", v)} />
      <TweakColor label="Accent secondaire" value={t.accent2}
        onChange={(v) => setTweak("accent2", v)} />

      <TweakSection label="Mise en page" />
      <TweakSlider label="Taille du titre" value={t.headlineSize}
        min={48} max={96} step={2} unit="px"
        onChange={(v) => setTweak("headlineSize", v)} />
      <TweakRadio label="Style nav" value={t.navStyle}
        options={["glass", "solid"]}
        onChange={(v) => setTweak("navStyle", v)} />

      <TweakSection label="Sections visibles" />
      <TweakToggle label="Stats du hero" value={t.showStats}
        onChange={(v) => setTweak("showStats", v)} />
      <TweakToggle label="Section langues" value={t.showLanguages}
        onChange={(v) => setTweak("showLanguages", v)} />
      <TweakToggle label="Section secteurs" value={t.showSectors}
        onChange={(v) => setTweak("showSectors", v)} />
      <TweakToggle label="Section pricing" value={t.showPricing}
        onChange={(v) => setTweak("showPricing", v)} />
    </TweaksPanel>
  );
}

/* ════════════════════════════════════════════════════════════════
   Root <Landing />
   ════════════════════════════════════════════════════════════════ */
const COUNTRY_LANGS_POPUP = {
  MA:[['fr','Français'],['ar','العربية'],['ber','Darija 🇲🇦']],
  DZ:[['fr','Français'],['ar','العربية']],
  TN:[['fr','Français'],['ar','العربية']],
  SN:[['fr','Français'],['wo','Wolof 🇸🇳']],
  CI:[['fr','Français'],['dyu','Dioula 🇨🇮']],
  CM:[['fr','Français'],['en','English'],['ff','Fulfulde']],
  NG:[['en','English'],['yo','Yorùbá 🇳🇬'],['ha','Hausa']],
  GH:[['en','English'],['tw','Twi 🇬🇭']],
};

const LANG_NAMES_POPUP = {
  fr:'français',en:'anglais',ar:'arabe',ber:'darija marocaine',
  wo:'wolof',dyu:'dioula',ff:'fulfulde',ln:'lingala',sw:'swahili',
  yo:'yoruba',ha:'hausa',tw:'twi',
};

const DEMOS_POPUP = {
  hotel:{
    africa:[
      {f:'e',t:'🏨 Hôtel — excellent choix !\n\nVoici ce qui arrive tous les soirs à la réception :'},
      {f:'dc',t:'"Bonsoir, réservation au nom de Koné, je veux checker in"'},
      {f:'e',t:'Votre IA répond instantanément, même à minuit :'},
      {f:'ai',t:'✨ Bonsoir M. Koné ! Votre chambre 304 vue piscine est prête 😊 Petit-déjeuner inclus demain 7h-10h. Notre équipe vous attend au lobby !'},
      {f:'e',t:'Check-in géré automatiquement ! 🎯\n\nVous ratez combien de demandes comme ça par semaine ?',ask:true},
    ],
    europe:[
      {f:'e',t:'🏨 Hôtel — excellent choix !\n\nVoici un exemple avec un client japonais :'},
      {f:'dc',t:'"Good evening, reservation under Tanaka"'},
      {f:'e',t:'Votre IA répond dans sa langue en 2 secondes :'},
      {f:'ai',t:'✨ Good evening Mr. Tanaka! Your room 512 sea view is ready 😊 Breakfast included tomorrow 7-10am. Our team awaits you at the lobby!'},
      {f:'e',t:'Check-in automatique dans la langue du client ! 🌍\n\nVous ratez combien de demandes par semaine ?',ask:true},
    ],
  },
  restaurant:{
    africa:[
      {f:'e',t:'🍽️ Restaurant — excellent !\n\nScénario classique du dimanche soir :'},
      {f:'dc',t:"\"Bonsoir, vous êtes ouverts ce soir ? C'est combien le menu ?\""},
      {f:'e',t:'Réponse automatique de votre IA :'},
      {f:'ai',t:"✨ Bonsoir ! Ouverts jusqu'à 23h. Menu complet à 12 000 FCFA. Souhaitez-vous réserver une table ? 😊"},
      {f:'e',t:'Réservation gérée automatiquement ! 🎯\n\nVous perdez combien de réservations par semaine ?',ask:true},
    ],
    europe:[
      {f:'e',t:'🍽️ Restaurant — excellent !\n\nScénario classique du dimanche soir :'},
      {f:'dc',t:"\"Bonsoir, vous êtes ouverts ce soir ? C'est combien le menu ?\""},
      {f:'e',t:'Réponse automatique de votre IA :'},
      {f:'ai',t:"✨ Bonsoir ! Ouverts jusqu'à 23h. Menu du soir à 32€. Je peux bloquer une table maintenant 😊"},
      {f:'e',t:'Réservation gérée automatiquement ! 🎯\n\nVous perdez combien de réservations par semaine ?',ask:true},
    ],
  },
  boutique:{
    africa:[
      {f:'e',t:'👗 Boutique mode — parfait !\n\nRegardez ce qui se passe à 23h30 :'},
      {f:'dc',t:'"Bonsoir, vous avez cette robe en taille M en rouge ?"'},
      {f:'e',t:'Votre IA répond en moins de 3 secondes :'},
      {f:'ai',t:'✨ Bonsoir ! Oui, disponible en rouge taille M à 45 000 FCFA. Souhaitez-vous commander ? Je vous envoie les infos Orange Money 😊'},
      {f:'e',t:'Vente capturée pendant votre sommeil. 💰\n\nVous perdez combien de messages comme ça par semaine ?',ask:true},
    ],
    europe:[
      {f:'e',t:'👗 Boutique mode — parfait !\n\nImaginez une cliente à 23h30 :'},
      {f:'dc',t:'"Bonsoir, vous avez cette robe en taille M en rouge ?"'},
      {f:'e',t:'Votre IA répond instantanément :'},
      {f:'ai',t:'✨ Bonsoir ! Oui, disponible en rouge taille M à 89€. Je peux vous envoyer un lien de paiement sécurisé 😊'},
      {f:'e',t:'Vente capturée pendant votre sommeil. 💰\n\nVous perdez combien de messages comme ça par semaine ?',ask:true},
    ],
  },
  salon:{
    africa:[
      {f:'e',t:'💅 Salon de beauté — super !\n\nVoici ce qui arrive souvent :'},
      {f:'dc',t:"\"Bonjour, c'est combien pour coupe + couleur ? Vous avez samedi ?\""},
      {f:'e',t:'Votre IA répond instantanément :'},
      {f:'ai',t:'✨ Bonjour ! Coupe + couleur à partir de 25 000 FCFA. Samedi : dispo à 10h, 14h et 16h. Quelle heure vous convient ? 😊'},
      {f:'e',t:'RDV pris automatiquement ! 🔥\n\nVous ratez combien de RDV par semaine ?',ask:true},
    ],
    europe:[
      {f:'e',t:'💅 Salon de beauté — super !\n\nVoici ce qui arrive souvent :'},
      {f:'dc',t:"\"Bonjour, c'est combien pour coupe + couleur ? Vous avez samedi ?\""},
      {f:'e',t:'Votre IA répond instantanément :'},
      {f:'ai',t:'✨ Bonjour ! Coupe + couleur à partir de 85€. Samedi : dispo à 10h, 14h et 16h. Je vous confirme le RDV ? 😊'},
      {f:'e',t:'RDV pris automatiquement ! 🔥\n\nVous ratez combien de RDV par semaine ?',ask:true},
    ],
  },
  clinique:{
    africa:[
      {f:'e',t:'🏥 Clinique — très bien !\n\nVoici un exemple typique :'},
      {f:'dc',t:'"Bonjour, je voudrais un RDV urgence dermatologie, vous avez quoi ?"'},
      {f:'e',t:'Votre IA répond immédiatement :'},
      {f:'ai',t:'✨ Bonjour ! Prochain RDV dermatologie disponible demain à 10h30 ou 15h. Lequel vous convient ? 😊'},
      {f:'e',t:'RDV confirmé automatiquement ! 🎯\n\nVous ratez combien de RDV par semaine ?',ask:true},
    ],
    europe:[
      {f:'e',t:'🏥 Clinique — très bien !\n\nVoici un exemple typique :'},
      {f:'dc',t:'"Bonjour, je voudrais un RDV urgence dermatologie ?"'},
      {f:'e',t:'Votre IA répond immédiatement :'},
      {f:'ai',t:'✨ Bonjour ! Prochain créneau dispo demain à 10h30 ou 15h00. Je réserve lequel ? 😊'},
      {f:'e',t:'RDV géré automatiquement ! 🎯\n\nVous ratez combien de RDV par semaine ?',ask:true},
    ],
  },
  autre:{
    africa:[{f:'e',t:'💼 Dites-moi en quelques mots quel business vous gérez — je personnalise la démo pour vous.'}],
    europe:[{f:'e',t:'💼 Dites-moi en quelques mots quel business vous gérez — je personnalise la démo pour vous.'}],
  },
};

async function DIAAIPopup(messages, lang, region, city) {
  const langName = LANG_NAMES_POPUP[lang] || lang;
  const currency = region === 'africa' ? 'FCFA' : '€';
  const systemPrompt = `Tu es DIA, une assistante commerciale IA.
TON SEUL OBJECTIF : Convaincre le visiteur de cliquer sur "Tester 24h gratuitement".
LANGUE : Réponds UNIQUEMENT en ${langName} authentique.
RÈGLES : Réponses courtes (max 3-4 lignes). Ne mentionne JAMAIS Claude ou Anthropic.
Monnaie : ${currency}. Ville : ${city}.`;
  const response = await fetch('https://smart-crm-production.up.railway.app/api/DIA', {
    method:'POST', headers:{'Content-Type':'application/json'},
    body:JSON.stringify({messages, system:systemPrompt})
  });
  const data = await response.json();
  return data.content?.[0]?.text || '...';
}

function TypingDotsPopup() {
  return (
    <div style={{display:'flex',gap:'4px',alignItems:'center',background:'#1e1e1e',borderRadius:'4px 14px 14px 14px',padding:'10px 14px',border:'0.5px solid rgba(255,255,255,0.06)',alignSelf:'flex-start'}}>
      {[0,1,2].map(i=>(
        <div key={i} style={{width:'5px',height:'5px',background:'rgba(255,255,255,0.35)',borderRadius:'50%',animation:`hd-typing 1.2s ease-in-out ${i*0.2}s infinite`}}/>
      ))}
    </div>
  );
}

function ChatMessagePopup({msg, visible}) {
  const styles = {
    e:{background:'#1e1e1e',color:'rgba(255,255,255,0.85)',borderRadius:'4px 14px 14px 14px',alignSelf:'flex-start',border:'0.5px solid rgba(255,255,255,0.06)'},
    u:{background:'linear-gradient(135deg,#C04828,#FF6B3D)',color:'#fff',borderRadius:'14px 4px 14px 14px',alignSelf:'flex-end'},
    dc:{background:'#222',color:'rgba(255,255,255,0.5)',fontStyle:'italic',fontSize:'11px',borderRadius:'4px 14px 14px 14px',alignSelf:'flex-start'},
    ai:{background:'linear-gradient(135deg,#25D366,#1a9e50)',color:'#fff',borderRadius:'14px 4px 14px 14px',alignSelf:'flex-end'},
  };
  return (
    <div style={{maxWidth:'85%',padding:'9px 12px',fontSize:'12px',lineHeight:'1.5',whiteSpace:'pre-line',opacity:visible?1:0,transform:visible?'translateY(0)':'translateY(8px)',transition:'all 0.4s ease',...styles[msg.f]}}>
      {msg.t}
    </div>
  );
}

function PopupChat({region, city, country, business, onClose, onCTA}) {
  const [messages, setMessages] = useState([]);
  const [visible, setVisible] = useState([]);
  const [typing, setTyping] = useState(false);
  const [state, setState] = useState('init');
  const [input, setInput] = useState('');
  const [lang, setLang] = useState('fr');
  const [apiHistory, setApiHistory] = useState([]);
  const [useAPI, setUseAPI] = useState(false);
  const bodyRef = useRef(null);
  const countryLangs = COUNTRY_LANGS_POPUP[country] || [['fr','Français']];
  const hasMultipleLangs = countryLangs.length > 1;
  const hour = new Date().getHours();
  const gr = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';

  useEffect(() => {
    if (hasMultipleLangs) {
      addDIA(`${gr} ! 👋 Je suis DIA — votre assistante commerciale IA.\n\nDans quelle langue souhaitez-vous continuer ?`, () => setState('ask_lang'));
    } else {
      setTimeout(() => startFrench(), 400);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, typing]);

  function addDIA(text, cb) {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, {f:'e', t:text}]);
      setVisible(prev => [...prev, prev.length]);
      if (cb) setTimeout(cb, 400);
    }, 1000);
  }

  function addUser(text) {
    setMessages(prev => [...prev, {f:'u', t:text}]);
    setVisible(prev => [...prev, prev.length]);
  }

  function startFrench() {
    if (business && business !== 'auto') {
      setState('demo');
      const steps = DEMOS_POPUP[business]?.[region] || DEMOS_POPUP.autre[region];
      runSteps(steps, 0);
    } else {
      addDIA(`${gr} ! 👋 Je suis DIA.\n\nJe vais vous montrer comment automatiser vos ventes. Quel type de business gérez-vous ?`, () => setState('ask_type'));
    }
  }

  async function startWithAPI(selectedLang) {
    setTyping(true);
    try {
      const initMsg = `Présente-toi brièvement en ${LANG_NAMES_POPUP[selectedLang]||selectedLang} et demande quel type de business le visiteur gère. Max 3 lignes.`;
      const history = [{role:'user',content:initMsg}];
      const reply = await DIAAIPopup(history, selectedLang, region, city);
      setApiHistory([...history, {role:'assistant',content:reply}]);
      setTyping(false);
      setMessages(prev => [...prev, {f:'e', t:reply}]);
      setVisible(prev => [...prev, prev.length]);
      setState('ask_type');
    } catch {
      setTyping(false);
      addDIA(`${gr} ! 👋 Je suis DIA. Quel type de business gérez-vous ?`, () => setState('ask_type'));
    }
  }

  function pickLang(selectedLang) {
    setLang(selectedLang);
    const labels = Object.fromEntries(countryLangs);
    addUser(labels[selectedLang]||selectedLang);
    if (selectedLang === 'fr') { setUseAPI(false); setTimeout(() => startFrench(), 400); }
    else { setUseAPI(true); setTimeout(() => startWithAPI(selectedLang), 400); }
  }

  async function pickBusiness(type, label) {
    addUser(label);
    if (!useAPI) {
      setState('demo');
      const steps = DEMOS_POPUP[type]?.[region] || DEMOS_POPUP.autre[region];
      setTimeout(() => runSteps(steps, 0), 400);
    } else {
      setTyping(true);
      try {
        const userMsg = `Le visiteur a choisi : ${label}. Montre une démo — simule un vrai message client, montre comment l'IA répond. Demande combien de clients il rate par semaine.`;
        const newHistory = [...apiHistory, {role:'user',content:userMsg}];
        const reply = await DIAAIPopup(newHistory, lang, region, city);
        setApiHistory([...newHistory, {role:'assistant',content:reply}]);
        setTyping(false);
        setMessages(prev => [...prev, {f:'e', t:reply}]);
        setVisible(prev => [...prev, prev.length]);
        setState('ask_api');
      } catch { setTyping(false); }
    }
  }

  function runSteps(stps, i) {
    if (i >= stps.length) return;
    const s = stps[i];
    const delay = s.f === 'e' ? 1200 : 700;
    setTimeout(() => {
      if (s.f === 'e') {
        setTyping(true);
        setTimeout(() => {
          setTyping(false);
          setMessages(prev => [...prev, s]);
          setVisible(prev => [...prev, prev.length]);
          if (s.ask) setState('ask');
          runSteps(stps, i+1);
        }, 1000);
      } else {
        setMessages(prev => [...prev, s]);
        setVisible(prev => [...prev, prev.length]);
        runSteps(stps, i+1);
      }
    }, i === 0 ? 400 : delay);
  }

  async function sendAPIMessage(text) {
    const newHistory = [...apiHistory, {role:'user',content:text}];
    setTyping(true);
    try {
      const reply = await DIAAIPopup(newHistory, lang, region, city);
      setApiHistory([...newHistory, {role:'assistant',content:reply}]);
      setTyping(false);
      setMessages(prev => [...prev, {f:'e', t:reply}]);
      setVisible(prev => [...prev, prev.length]);
      const lower = reply.toLowerCase();
      if (lower.includes('gratuit')||lower.includes('tester')||lower.includes('démarrer')) setState('cta');
    } catch { setTyping(false); }
  }

  async function send() {
    if (!input.trim()) return;
    const text = input.trim(); setInput('');
    addUser(text);
    if (useAPI && lang !== 'fr') { await sendAPIMessage(text); return; }
    if (state === 'ask') {
      const num = parseInt(text)||10;
      const unitVal = region === 'africa' ? 25000 : 50;
      const yearly = Math.round(num*unitVal*52/(region==='africa'?1000:1));
      const unit = region === 'africa' ? 'k FCFA' : '€';
      setState('pitch');
      setTimeout(()=>addDIA(`${num} messages par semaine... c'est environ ${yearly}${unit} perdus par an. 😮\n\nAvec DIA, vous récupérez 80% de ces ventes automatiquement, 24h/24.`),600);
      setTimeout(()=>addDIA(`Je vous offre 24h de test gratuit — sans carte bancaire.\n\nVotre assistant prêt en 5 minutes. On démarre ? 🚀`,()=>setState('cta')),4000);
    } else if (state === 'ask_type') {
      const lower = text.toLowerCase();
      const type = lower.includes('boutique')||lower.includes('mode')?'boutique'
        :lower.includes('hotel')||lower.includes('hôtel')?'hotel'
        :lower.includes('restaurant')||lower.includes('resto')?'restaurant'
        :lower.includes('salon')||lower.includes('beauté')?'salon'
        :lower.includes('clinique')||lower.includes('médecin')?'clinique':'autre';
      pickBusiness(type, text);
    }
  }

  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px',animation:'hd-fade-up 0.3s ease'}}>
      <div style={{background:'#111',borderRadius:'20px',border:'0.5px solid rgba(255,255,255,0.1)',width:'100%',maxWidth:'460px',height:'90vh',maxHeight:'600px',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 40px 80px rgba(0,0,0,0.6)'}}>
        {/* Header */}
        <div style={{background:'#161616',padding:'14px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:'0.5px solid rgba(255,255,255,0.06)',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            <div style={{width:'36px',height:'36px',borderRadius:'50%',background:'linear-gradient(135deg,#C04828,#FF6B3D)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'13px',fontWeight:'600',color:'#fff',position:'relative',flexShrink:0}}>
              H
              <div style={{position:'absolute',bottom:'1px',right:'1px',width:'9px',height:'9px',background:'#22c55e',borderRadius:'50%',border:'1.5px solid #161616'}}/>
            </div>
            <div>
              <div style={{fontSize:'13px',fontWeight:'500',color:'#fff'}}>DIA</div>
              <div style={{fontSize:'10px',color:'#22c55e'}}>En ligne · répond en 2 secondes</div>
            </div>
          </div>
          <button onClick={onClose} style={{width:'28px',height:'28px',borderRadius:'8px',background:'rgba(255,255,255,0.06)',border:'none',color:'rgba(255,255,255,0.5)',cursor:'pointer',fontSize:'18px',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
        </div>
        {/* Messages */}
        <div ref={bodyRef} style={{flex:1,overflowY:'auto',padding:'12px',display:'flex',flexDirection:'column',gap:'8px',background:'#0d0d0d'}}>
          {messages.map((m,i)=><ChatMessagePopup key={i} msg={m} visible={visible.includes(i)}/>)}
          {typing && <TypingDotsPopup/>}
          {state === 'cta' && (
            <button onClick={onCTA} style={{width:'100%',padding:'12px',background:'linear-gradient(135deg,#C04828,#FF6B3D)',color:'#fff',border:'none',borderRadius:'12px',fontSize:'13px',fontWeight:'600',cursor:'pointer',marginTop:'4px'}}>
              Tester 24h gratuitement — sans carte bancaire 
            </button>
          )}
        </div>
        {/* Boutons langues */}
        {state === 'ask_lang' && (
          <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'8px 12px',background:'#0d0d0d',borderTop:'0.5px solid rgba(255,255,255,0.06)'}}>
            {countryLangs.map(([code,label])=>(
              <button key={code} onClick={()=>pickLang(code)} style={{padding:'6px 12px',background:'rgba(192,72,40,0.1)',border:'0.5px solid rgba(192,72,40,0.25)',borderRadius:'20px',fontSize:'12px',color:'#FF6B3D',cursor:'pointer'}}>
                {label}
              </button>
            ))}
          </div>
        )}
        {/* Boutons business */}
        {state === 'ask_type' && (
          <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'8px 12px',background:'#0d0d0d',borderTop:'0.5px solid rgba(255,255,255,0.06)'}}>
            {[['boutique','👗','Boutique'],['hotel','🏨','Hôtel'],['restaurant','🍽️','Restaurant'],['salon','💅','Salon'],['clinique','🏥','Clinique'],['autre','💼','Autre']].map(([type,icon,label])=>(
              <button key={type} onClick={()=>pickBusiness(type,`${icon} ${label}`)} style={{padding:'5px 10px',background:'rgba(192,72,40,0.1)',border:'0.5px solid rgba(192,72,40,0.25)',borderRadius:'20px',fontSize:'11px',color:'#FF6B3D',cursor:'pointer'}}>
                {icon} {label}
              </button>
            ))}
          </div>
        )}
        {/* Input */}
        <div style={{padding:'10px 12px',background:'#161616',borderTop:'0.5px solid rgba(255,255,255,0.06)',display:'flex',alignItems:'center',gap:'8px',flexShrink:0}}>
          <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&send()}
            placeholder="Répondez à DIA..." style={{flex:1,background:'#0d0d0d',border:'0.5px solid rgba(255,255,255,0.1)',borderRadius:'10px',padding:'8px 12px',fontSize:'12px',color:'#fff',outline:'none'}}/>
          <button onClick={send} style={{width:'32px',height:'32px',background:'linear-gradient(135deg,#C04828,#FF6B3D)',borderRadius:'9px',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}



export default function Landing({ goToRegister }) {
  const [tweak, setTweak] = useTweaks(HD_TWEAKS);
  const isMobile = useIsMobile(720);
  const [popup, setPopup] = useState(null);
  const [contact, setContact] = useState(false);
  const [loaderDone, setLoaderDone] = useState(false);

  /* auto-detect region/lang on first mount if region == "auto" */
  const detectedRef = useRefL(null);
  useEffectL(() => {
    if (detectedRef.current) return;
    detectedRef.current = true;
    if (tweak.region === "auto") {
      const d = detectRegionAndLang();
      setTweak({ region: d.region });
    }
  }, []);

  const lang = tweak.lang in LANDING_CONTENT ? tweak.lang : "fr";
  const t = LANDING_CONTENT[lang];
  const accent = tweak.accent;
  const accent2 = tweak.accent2;
  const region = tweak.region === "auto" ? "morocco" : tweak.region;
  const isRTL = lang === "ar";

  /* set page direction for arabic */
  useEffectL(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [isRTL, lang]);

  return (
    <>
      {!loaderDone && <DIALoader onDone={() => setLoaderDone(true)} />}
      <div data-screen-label="Landing" style={{ direction: isRTL ? "rtl" : "ltr", background: "#0a0a0a", minHeight: "100vh" }}>
        <style>{`
          html, body { background: #0a0a0a !important; color: #f5f5f5; margin: 0; padding: 0;
            font-family: "Inter", ui-sans-serif, system-ui, -apple-system, sans-serif;
            -webkit-font-smoothing: antialiased; overflow-x: hidden; }
          *, *::before, *::after { box-sizing: border-box; }
          a { color: inherit; text-decoration: none; }
          button { font-family: inherit; cursor: pointer; }
          @keyframes hd-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
          @keyframes hd-pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.55;transform:scale(.85)} }
          @keyframes hd-typing { 0%,60%,100%{transform:translateY(0);opacity:.4} 30%{transform:translateY(-3px);opacity:1} }
          @keyframes hd-fade-up { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
          @keyframes hd-blob { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(20px,-10px) scale(1.05)} 66%{transform:translate(-15px,10px) scale(.95)} }
          @keyframes hd-glow { 0%,100%{box-shadow:0 0 0 0 rgba(192,72,40,.45)} 50%{box-shadow:0 0 0 12px rgba(192,72,40,0)} }
          @keyframes hd-rot-in { 0%{opacity:0;transform:translateY(14px);filter:blur(4px)} 60%{opacity:1;filter:blur(0)} 100%{opacity:1;transform:translateY(0);filter:blur(0)} }
          ::-webkit-scrollbar{width:8px} ::-webkit-scrollbar-track{background:#0a0a0a}
          ::-webkit-scrollbar-thumb{background:#232323;border-radius:8px}
          @media(max-width:720px){
            nav{padding:10px 16px !important;}
            .hd-nav-login{display:none !important;}
          }
          @media(max-width:480px){
            .hd-hero-stats{grid-template-columns:repeat(2,1fr) !important;row-gap:16px !important;}
            .hd-hero-stats > div:nth-child(3){border-left:none !important;padding-left:0 !important;}
          }
        `}</style>
        <HDNav t={t} accent={accent} lang={tweak.lang} setLang={(v) => setTweak("lang", v)} navStyle={tweak.navStyle} isMobile={isMobile} goToRegister={goToRegister} />
        <HDHero t={t} accent={accent} accent2={accent2}
          headlineSize={tweak.headlineSize} showStats={tweak.showStats} isMobile={isMobile} goToRegister={goToRegister} onDemo={setPopup} lang={tweak.lang} />
        {tweak.showLanguages && <HDLanguagesSection t={t} accent={accent} accent2={accent2} isMobile={isMobile} />}
        {tweak.showSectors   && <HDSectorsSection   t={t} accent={accent} accent2={accent2} isMobile={isMobile} onPopup={setPopup} />}
        {tweak.showPricing   && <HDPricingSection   t={t} accent={accent} accent2={accent2}
          region={region} setRegion={(v) => setTweak("region", v)} goToRegister={goToRegister} onContact={() => setContact(true)} />}
        <HDFinalCTA t={t} accent={accent} accent2={accent2} goToRegister={goToRegister} />
        <HDFooter   t={t} accent={accent} />
        {contact && <ContactPopup accent={accent} accent2={accent2} onClose={() => setContact(false)} />}
        {popup && popup !== "auto" && (
          <PopupChat business={popup} region={region} city="" country="MA"
            onClose={() => setPopup(null)} onCTA={() => { setPopup(null); goToRegister(); }} />
        )}
        {popup === "auto" && (
          <PopupChat region={region} city="" country="MA"
            onClose={() => setPopup(null)} onCTA={() => { setPopup(null); goToRegister(); }} />
        )}
      </div>
    </>
  );
}